import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Button, IconButton, Paper, Stack, Switch } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import DevicesIcon from '@mui/icons-material/Devices';
import SettingsIcon from '@mui/icons-material/Settings';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import TimerIcon from '@mui/icons-material/Timer';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import NoSleep from 'nosleep.js';
import TimerButton from './Button/TimerButton';
import time2hiitLogoFinal from './img/time2hiit_logo_final.svg';
import time2hiitLogoLight from './img/time2hiit_logo_light.svg';
import './App.css';
import { EXERCISE_LIBRARY, EXERCISE_EN_EQUIVALENTS, FOCUS_IDS, normalizeCustomExercises, getExerciseFocus, searchExercises } from './exercises';
import { useAccountSync } from './useAccountSync';
import { AccountPanel, SharedPlanDialog } from './AccountPanel';
import { ACCOUNT_TRANSLATIONS } from './accountTranslations';
import ShareIcon from '@mui/icons-material/Share';

function resolveApiBase() {
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE;
  }

  const { protocol, hostname, port, origin } = window.location;
  const isLocalDev = port === '3000' || port === '3010';
  if (isLocalDev) {
    return `${protocol}//${hostname}:3011`;
  }
  if (port === '3011') return origin;

  const isCapacitorOrigin = protocol === 'capacitor:' || protocol === 'ionic:' || protocol === 'file:';
  const isLocalCapacitorHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isCapacitorOrigin || isLocalCapacitorHost) {
    return 'https://timer.uberli.ch';
  }

  return origin;
}

const API_BASE = resolveApiBase();
const LAST_INDIVIDUAL_KEY = 'xtimer:last-individual';
const START_COUNTDOWN_MS = 10_000;
const PLAN_LIBRARY_KEY = 'xtimer:plan-library';
const TEMPLATE_LIBRARY_KEY = 'xtimer:template-library';
const CUSTOM_EXERCISES_KEY = 'xtimer:custom-exercises';
const THEME_MODE_KEY = 'xtimer:theme-mode';
const LANGUAGE_KEY = 'xtimer:language';
const TRAINING_LOG_KEY = 'xtimer:training-log';
const ACTIVE_OFFLINE_SESSION_KEY = 'xtimer:active-offline-session';
const ACTIVE_OFFLINE_SESSION_MAX_AGE_MS = 8 * 60 * 60 * 1000;
const ACTIVE_GREEN = '#6cab52';
const NETWORK_TIMEOUT_MS = 4500;
const WS_RECONNECT_BASE_MS = 600;
const WS_RECONNECT_MAX_MS = 5000;
const IS_NATIVE = Capacitor.isNativePlatform();
const WearSync = registerPlugin('WearSync');

async function prefGet(key) {
  try {
    const { value } = await Preferences.get({ key });
    return value;
  } catch (_e) {
    return null;
  }
}

async function prefSet(key, value) {
  try {
    await Preferences.set({ key, value });
  } catch (_e) {
    // silent — localStorage is the fallback
  }
}

async function fetchWithTimeout(url, options = {}, timeoutMs = NETWORK_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

const MUSCLE_AND_STRENGTH_EXERCISES_URL = 'https://www.muscleandstrength.com/exercises';

const EXERCISE_REFERENCE_LINKS = {
  'dumbbell bench press': 'https://www.muscleandstrength.com/exercises/dumbbell-bench-press.html',
  'incline bench press': 'https://www.muscleandstrength.com/exercises/incline-bench-press.html',
  bankdruecken: 'https://www.muscleandstrength.com/exercises/barbell-bench-press.html',
  schulterdruecken: 'https://www.muscleandstrength.com/exercises/military-press.html',
  'overhead press': 'https://www.muscleandstrength.com/exercises/military-press.html',
  'arnold press': 'https://www.muscleandstrength.com/exercises/seated-arnold-press.html',
  rudern: 'https://www.muscleandstrength.com/exercises/barbell-row.html',
  klimmzuege: 'https://www.muscleandstrength.com/exercises/pull-ups.html',
  liegestuetz: 'https://www.muscleandstrength.com/exercises/push-ups.html',
  dips: 'https://www.muscleandstrength.com/exercises/dips.html',
  'kurzhantel rudern': 'https://www.muscleandstrength.com/exercises/dumbbell-row.html',
  latzug: 'https://www.muscleandstrength.com/exercises/lat-pulldown.html',
  'face pull': 'https://www.muscleandstrength.com/exercises/face-pull.html',
  seitheben: 'https://www.muscleandstrength.com/exercises/dumbbell-lateral-raise.html',
  'upright row': 'https://www.muscleandstrength.com/exercises/upright-row.html',
  'dumbbell shrug': 'https://www.muscleandstrength.com/exercises/dumbbell-shrugs.html',
  'hammer curls': 'https://www.muscleandstrength.com/exercises/hammer-curl.html',
  bizepscurls: 'https://www.muscleandstrength.com/exercises/bicep-curl.html',
  skullcrusher: 'https://www.muscleandstrength.com/exercises/ez-bar-skullcrusher.html',
  'enges bankdruecken': 'https://www.muscleandstrength.com/exercises/close-grip-bench-press.html',
  'chest fly': 'https://www.muscleandstrength.com/exercises/dumbbell-chest-fly.html',
  'inverted row': 'https://www.muscleandstrength.com/exercises/inverted-row.html',
  kniebeugen: 'https://www.muscleandstrength.com/exercises/squat.html',
  ausfallschritte: 'https://www.muscleandstrength.com/exercises/lunges.html',
  'goblet squat': 'https://www.muscleandstrength.com/exercises/dumbbell-goblet-squat.html',
  'front squat': 'https://www.muscleandstrength.com/exercises/front-squat.html',
  'back squat': 'https://www.muscleandstrength.com/exercises/squat.html',
  beinpresse: 'https://www.muscleandstrength.com/exercises/45-degree-leg-press.html',
  'adductor machine': 'https://www.muscleandstrength.com/exercises/adductors.html',
  'abductor machine': 'https://www.muscleandstrength.com/exercises/abductors.html',
  'hack squat': 'https://www.muscleandstrength.com/exercises/hack-squat.html',
  'step-up': 'https://www.muscleandstrength.com/exercises/dumbbell-step-ups.html',
  'rumaenisches kreuzheben': 'https://www.muscleandstrength.com/exercises/stiff-leg-deadlift-aka-romanian-deadlift.html',
  'hip thrust': 'https://www.muscleandstrength.com/exercises/barbell-hip-thrust.html',
  'bulgarian split squat': 'https://www.muscleandstrength.com/exercises/bulgarian-split-squat.html',
  'jumping lunges': 'https://www.muscleandstrength.com/exercises/jumping-lunges.html',
  'leg curl': 'https://www.muscleandstrength.com/exercises/leg-curl.html',
  'leg extension': 'https://www.muscleandstrength.com/exercises/leg-extension.html',
  wadenheben: 'https://www.muscleandstrength.com/exercises/calf-raise.html',
  'jump squat': 'https://www.muscleandstrength.com/exercises/jump-squat.html',
  'skater jump': 'https://www.muscleandstrength.com/exercises/skater-jumps.html',
  'hamstring slide': 'https://www.muscleandstrength.com/exercises/slider-hamstring-curls.html',
  'nordic curl': 'https://www.muscleandstrength.com/exercises/nordic-curl.html',
  'glute bridge': 'https://www.muscleandstrength.com/exercises/glute-bridge.html',
  deadlift: 'https://www.muscleandstrength.com/exercises/deadlifts.html',
  'sumo deadlift': 'https://www.muscleandstrength.com/exercises/sumo-deadlift.html',
  'lying leg raise': 'https://www.muscleandstrength.com/exercises/lying-floor-leg-raise.html',
  'cable crunch': 'https://www.muscleandstrength.com/exercises/cable-crunch.html',
  'weighted crunch': 'https://www.muscleandstrength.com/exercises/weighted-crunch.html',
  plank: 'https://www.muscleandstrength.com/exercises/plank.html',
  'dead bug': 'https://www.muscleandstrength.com/exercises/dead-bug.html',
  'hanging knee raise': 'https://www.muscleandstrength.com/exercises/hanging-knee-raise.html',
  'russian twist': 'https://www.muscleandstrength.com/exercises/russian-twist.html',
  'side plank': 'https://www.muscleandstrength.com/exercises/side-plank.html',
  'v-up': 'https://www.muscleandstrength.com/exercises/v-ups.html',
  'sit-up': 'https://www.muscleandstrength.com/exercises/sit-up.html',
  'mountain climber': 'https://www.muscleandstrength.com/exercises/mountain-climber.html',
  'bicycle crunch': 'https://www.muscleandstrength.com/exercises/bicycle-crunch.html',
  'bird dog': 'https://www.muscleandstrength.com/exercises/bird-dog.html',
  'pallof press': 'https://www.muscleandstrength.com/exercises/pallof-press.html',
  'ab wheel rollout': 'https://www.muscleandstrength.com/exercises/ab-wheel-rollout.html',
  'reverse crunch': 'https://www.muscleandstrength.com/exercises/reverse-crunch.html',
  woodchopper: 'https://www.muscleandstrength.com/exercises/woodchops.html',
  'flutter kicks': 'https://www.muscleandstrength.com/exercises/flutter-kicks.html',
  burpees: 'https://www.muscleandstrength.com/exercises/burpees.html',
  thruster: 'https://www.muscleandstrength.com/exercises/barbell-thrusters.html',
  'kettlebell swing': 'https://www.muscleandstrength.com/exercises/kettlebell-swing.html',
  'farmer carry': 'https://www.muscleandstrength.com/exercises/farmers-carry.html',
  'wall ball': 'https://www.muscleandstrength.com/exercises/wall-ball-shot.html',
  'push press': 'https://www.muscleandstrength.com/exercises/push-press.html',
  'jump rope double under': 'https://www.muscleandstrength.com/exercises/jump-rope.html',
  'bear crawl': 'https://www.muscleandstrength.com/exercises/bear-crawl.html'
};

function normalizeExerciseName(name) {
  return String(name || '').trim().replace(/\s+/g, ' ');
}

function getExerciseReferenceUrl(name) {
  const normalized = normalizeExerciseName(name).toLowerCase();
  if (!normalized) return MUSCLE_AND_STRENGTH_EXERCISES_URL;
  if (EXERCISE_REFERENCE_LINKS[normalized]) return EXERCISE_REFERENCE_LINKS[normalized];

  const english = EXERCISE_EN_EQUIVALENTS[normalized]?.toLowerCase();
  if (english && EXERCISE_REFERENCE_LINKS[english]) {
    return EXERCISE_REFERENCE_LINKS[english];
  }

  return MUSCLE_AND_STRENGTH_EXERCISES_URL;
}

function normalizeExerciseMode(mode) {
  const normalized = String(mode || '').trim().toUpperCase();
  if (normalized === 'SZ' || normalized === 'LR') return normalized;
  return 'NR';
}

function sanitizeWeightInput(value, finalize = false) {
  const raw = String(value ?? '').replace(',', '.');
  let normalized = raw.replace(/[^0-9.]/g, '');
  const firstDot = normalized.indexOf('.');
  if (firstDot >= 0) {
    normalized = `${normalized.slice(0, firstDot + 1)}${normalized.slice(firstDot + 1).replace(/\./g, '')}`;
  }

  if (!finalize) return normalized;
  if (!normalized) return '';

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return '';
  const rounded = Math.round(parsed * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function isSetDrivenPlan(config) {
  return config?.name === 'circuit210' || ((config?.sets || 0) > (config?.rounds || 0));
}

function getConfiguredPlanSlotCount(config) {
  return Math.max(1, isSetDrivenPlan(config) ? (Number(config?.sets) || 1) : (Number(config?.rounds) || 1));
}

function getConfiguredPreviewBlockCount(config, planLength = 0) {
  if (isSetDrivenPlan(config)) {
    return Math.max(1, getConfiguredPlanSlotCount(config), planLength || 0);
  }
  const rounds = Math.max(1, Number(config?.rounds) || 1);
  const sets = Math.max(1, Number(config?.sets) || 1);
  return rounds * sets;
}

function getProgressPlanSlotIndex(config, state) {
  return isSetDrivenPlan(config)
    ? Math.max(0, (state?.currentSet || 1) - 1)
    : Math.max(0, (state?.currentRound || 1) - 1);
}

function getProgressPreviewBlockIndex(config, state) {
  if (isSetDrivenPlan(config)) {
    return Math.max(0, (state?.currentSet || 1) - 1);
  }
  const sets = Math.max(1, Number(config?.sets) || 1);
  const roundIndex = Math.max(0, (state?.currentRound || 1) - 1);
  const setIndex = Math.max(0, Math.min(sets - 1, (state?.currentSet || 1) - 1));
  return (roundIndex * sets) + setIndex;
}

function getCurrentPlanSlotIndex(config, state, planLength) {
  if (!planLength) return -1;
  const slotIndex = getProgressPlanSlotIndex(config, state);
  if (config?.dynamicTraining) {
    return Math.min(slotIndex, planLength - 1);
  }
  return slotIndex % planLength;
}

function createPreviewStateForSlot(config, state, slotIndex, phase = 'idle') {
  const safeSlotIndex = Math.max(0, slotIndex);
  const baseState = {
    ...state,
    phase,
    segmentInSet: 1,
    remainingMs: 0,
    running: false,
    blockDurationMs: 0,
    blockStartedAt: null,
    updatedAt: Date.now()
  };

  if (isSetDrivenPlan(config)) {
    return {
      ...baseState,
      currentRound: 1,
      currentSet: safeSlotIndex + 1
    };
  }

  const sets = Math.max(1, Number(config?.sets) || 1);
  return {
    ...baseState,
    currentRound: Math.floor(safeSlotIndex / sets) + 1,
    currentSet: (safeSlotIndex % sets) + 1
  };
}

function stepPreviewState(config, state, delta, planLength = 0) {
  const totalSlots = getConfiguredPreviewBlockCount(config, planLength);
  const currentIndex = getProgressPreviewBlockIndex(config, state);
  const nextIndex = (currentIndex + delta + totalSlots) % totalSlots;
  const nextPhase = state?.phase === 'done' ? 'done' : 'idle';
  return createPreviewStateForSlot(config, state, nextIndex, nextPhase);
}

function getPerExerciseMode(planExercises, config, state, segmentInSet = 1) {
  if (!Array.isArray(planExercises) || !planExercises.length) return 'NR';
  const baseIndex = getCurrentPlanSlotIndex(config, state, planExercises.length);
  if (baseIndex < 0) return 'NR';
  const baseExercise = planExercises[baseIndex];
  const baseMode = normalizeExerciseMode(baseExercise?.mode);
  if (baseMode === 'SZ' && segmentInSet === 2) {
    if (baseExercise?.supersetExercise?.name) return 'SZ';
    return 'NR';
  }
  if (baseMode === 'SZ' && !baseExercise?.supersetExercise?.name) return 'NR';
  return baseMode;
}

function getActiveBlockTiming(config, mode, segmentInSet = 1) {
  if (mode === 'SZ' || mode === 'LR') {
    return {
      isSuperset: true,
      durationMs: Math.max(1000, (Number(config?.activeMs) || 0) - 10_000),
      midPauseMs: 5000,
      segmentInSet
    };
  }

  if (!config?.supersetEnabled) {
    return {
      isSuperset: false,
      durationMs: Number(config?.activeMs) || 1000,
      midPauseMs: 0,
      segmentInSet: 1
    };
  }

  const { firstHalfMs, secondHalfMs, midPauseMs } = getSupersetDurations(config.activeMs);
  return {
    isSuperset: true,
    durationMs: segmentInSet === 2 ? secondHalfMs : firstHalfMs,
    midPauseMs,
    segmentInSet
  };
}

const EQUIPMENT_OPTIONS = {
  db: { id: 'db', short: 'KH', icon: '🏋️' },
  bb: { id: 'bb', short: 'LH', icon: '🏋️' },
  plate: { id: 'plate', short: 'GP', icon: '💿' },
  kb: { id: 'kb', short: 'KB', icon: '🔔' },
  machine: { id: 'machine', short: 'GER', icon: '⚙️' }
};

function getAllowedEquipmentIds(exerciseName) {
  const name = String(exerciseName || '').toLowerCase();
  const machineKeywords = ['air bike', 'row sprint', 'ski erg', 'latzug', 'beinpresse', 'leg curl', 'leg extension'];
  if (machineKeywords.some((keyword) => name.includes(keyword))) {
    return ['machine'];
  }
  if (name.includes('kettlebell')) {
    return ['kb'];
  }
  if (name.includes('dumbbell') || name.includes('kurzhantel')) {
    return ['db'];
  }
  const bodyweightKeywords = ['burpees', 'jump rope', 'bear crawl', 'plank', 'sit-up', 'mountain climber', 'klimmz', 'liegestuetz', 'dips'];
  if (bodyweightKeywords.some((keyword) => name.includes(keyword))) {
    return ['db', 'plate', 'kb'];
  }
  return ['db', 'bb', 'plate', 'kb', 'machine'];
}

function normalizePlanExercise(entry) {
  const name = normalizeExerciseName(entry?.name);
  const allowed = getAllowedEquipmentIds(name);
  const rawWeight = entry?.weight ?? entry?.load ?? '';
  const weight = sanitizeWeightInput(rawWeight, false);
  const equipment = allowed.includes(entry?.equipment) ? entry.equipment : allowed[0];
  const mode = normalizeExerciseMode(entry?.mode);
  const result = { name, equipment, weight, mode, focus: getExerciseFocus(entry) };
  if (mode === 'SZ' && entry?.supersetExercise?.name) {
    const szName = normalizeExerciseName(entry.supersetExercise.name);
    const szAllowed = getAllowedEquipmentIds(szName);
    const szWeight = sanitizeWeightInput(entry.supersetExercise?.weight ?? '', false);
    const szEquipment = szAllowed.includes(entry.supersetExercise?.equipment) ? entry.supersetExercise.equipment : szAllowed[0];
    result.supersetExercise = { name: szName, equipment: szEquipment, weight: szWeight, focus: getExerciseFocus(entry.supersetExercise) };
  }
  return result;
}

function toSupersetExercise(entry) {
  const normalized = normalizePlanExercise(entry);
  if (!normalized.name) return null;
  return {
    name: normalized.name,
    focus: normalized.focus,
    equipment: normalized.equipment,
    weight: normalized.weight
  };
}

function isSameExerciseName(a, b) {
  return normalizeExerciseName(a).toLowerCase() === normalizeExerciseName(b).toLowerCase();
}

function findMatchingExerciseEntry(entries, name) {
  return flattenPlanExercises(entries).find((entry) => isSameExerciseName(entry.name, name)) || null;
}

function buildDynamicExerciseEntry(name, fallbackEntry, historyEntry) {
  const normalizedName = normalizeExerciseName(name);
  const allowed = getAllowedEquipmentIds(normalizedName);
  const preferredEquipment = allowed.includes(fallbackEntry?.equipment)
    ? fallbackEntry.equipment
    : (allowed.includes(historyEntry?.equipment) ? historyEntry.equipment : allowed[0]);

  return {
    name: normalizedName,
    focus: getExerciseFocus(historyEntry || fallbackEntry || { name: normalizedName }),
    equipment: preferredEquipment,
    weight: fallbackEntry && isSameExerciseName(fallbackEntry.name, normalizedName)
      ? fallbackEntry.weight
      : (historyEntry && isSameExerciseName(historyEntry.name, normalizedName) ? historyEntry.weight : '')
  };
}

function normalizePlanExercises(entries) {
  if (!Array.isArray(entries)) return [];

  const normalizedEntries = [];
  for (let i = 0; i < entries.length; i += 1) {
    const current = normalizePlanExercise(entries[i]);
    if (!current.name) continue;

    if (current.mode !== 'SZ') {
      normalizedEntries.push(current);
      continue;
    }

    if (current.supersetExercise?.name) {
      normalizedEntries.push(current);
      const next = normalizePlanExercise(entries[i + 1]);
      if (next.name && isSameExerciseName(next.name, current.supersetExercise.name)) {
        i += 1;
      }
      continue;
    }

    const nextEntry = normalizePlanExercise(entries[i + 1]);
    const next = toSupersetExercise(nextEntry);
    if (next) {
      normalizedEntries.push(normalizePlanExercise({
        ...current,
        supersetExercise: next
      }));
      if (nextEntry.mode === 'SZ' && nextEntry.supersetExercise?.name) {
        normalizedEntries.push(normalizePlanExercise({ ...nextEntry.supersetExercise, mode: 'NR' }));
      }
      i += 1;
      continue;
    }

    normalizedEntries.push(normalizePlanExercise({ ...current, mode: 'NR' }));
  }

  return normalizedEntries;
}

function flattenPlanExercises(entries) {
  return normalizePlanExercises(entries).flatMap((exercise) => {
    const rows = [exercise];
    if (normalizeExerciseMode(exercise.mode) === 'SZ' && exercise.supersetExercise?.name) {
      rows.push(normalizePlanExercise({ ...exercise.supersetExercise, mode: 'NR' }));
    }
    return rows;
  });
}

function getRestBlockDuration(config, mode) {
  const restMs = Number(config?.restMs) || 1000;
  return mode === 'SZ' ? Math.max(1000, restMs - 5000) : restMs;
}

const TRANSLATIONS = {
  de: {
    defaultSessionTitle: 'HIIT-Session',
    invalidSession: 'Ungultige Session',
    offlineJoinDisabled: 'Keine Internetverbindung',
    offlineStatus: 'Offline: keine Internetverbindung',
    join: 'Beitreten',
    joinByCode: 'Per Code',
    joinBySearch: 'Suchen',
    noResults: 'Keine Sessions gefunden',
    searchPlaceholder: 'Session, Fokus oder Code',
    trainingCompleteA: 'Training abgeschlossen',
    trainingCompleteB: 'Gut gemacht',
    audioUnlock: 'Ton aktivieren',
    wakeLockUnlock: 'Bildschirm aktiv halten',
    menuStartSession: 'Session starten',
    menuJoinSession: 'Session beitreten',
    menuAddDevice: 'Gerat hinzufugen',
    menuPlans: 'Trainingsplane',
    menuDeviceCode: 'Gerat',
    menuHome: 'Hauptmenu',
    menuSettings: 'Einstellungen',
    settingsLanguage: 'Sprache',
    settingsDesign: 'Design',
    languageGerman: 'Deutsch',
    languageEnglish: 'English',
    languageSpanish: 'Español',
    languagePortuguese: 'Português',
    themeDark: 'Dunkel',
    themeLight: 'Hell',
    themeAuto: 'Automatisch',
    roundLabel: 'Runde',
    setLabel: 'Satz',
    restart: 'Neu starten',
    trainingLog: 'Trainingslog',
    trainingLogEmpty: 'Noch keine Trainings absolviert.',
    trainingLogClearAll: 'Alle loschen',
    trainingLogExercises: '{n} Ubungen',
    trainingLogDelete: 'Loschen',
    planOverlayTitle: 'Trainingsplane',
    planNewTitle: 'Neuer Plan',
    planEditTitle: 'Plan bearbeiten',
    planEmpty: 'Noch keine Trainingsplane gespeichert.',
    planAddExercise: 'Ubung hinzufugen',
    planSave: 'Speichern',
    planBack: 'Zuruck',
    planNamePlaceholder: 'Mein Plan',
    planNameRequired: 'Bitte gib dem Trainingsplan einen Namen.',
    planSelectedLabel: 'Trainingsplan',
    planAddButton: '+ Trainingsplan',
    focusUpper: 'Oberkorper',
    focusLower: 'Unterkorper',
    focusCore: 'Core',
    focusFull: 'Ganzkorper',
    focusMix: 'Mix',
    selected: 'Ausgewahlt',
    select: 'Auswahlen',
    deviceCouple: 'Gerat koppeln',
    deviceAdd: 'Hinzufugen',
    deviceCodePlaceholder: 'Code',
    deviceAddHint: '(Gerat hinzufugen)',
    deviceOwnIdError: 'Eigene Gerate-ID kann nicht hinzugefugt werden',
    switchingOnline: 'Wechsle in den Online-Modus...',
    deviceAdded: 'Gerat {code} hinzugefugt',
    onlineUnavailable: 'Online nicht erreichbar',
    globalStart: 'Globalen Start planen',
    globalStartIn: 'Globaler Start in {countdown}',
    start: 'Starten',
    save: 'Speichern',
    warningTitle: 'Achtung',
    exercisesCount: '{current}/{required} Ubungen',
    presetClassic63: 'Classic 6×3',
    presetCircuit210: 'Circuit 2×10',
    presetSuperset: 'Supersatz',
    presetIndividual: 'Individuell',
    editorTitle: 'Editor',
    configRounds: 'Runden',
    configSets: 'Satze',
    configActive: 'Aktiv',
    configRest: 'Pause',
    configRoundRest: 'Rundenpause',
    configSuperset: 'Supersatz',
    configDynamicTraining: 'Dynamisches Training',
    dynamicNextExercise: 'Nächste Ubung wählen',
    controlPrev: 'Zuruck',
    controlPlay: 'Start',
    controlPause: 'Pause',
    controlSkip: 'Weiter',
    ariaClose: 'Schließen',
    ariaCloseSettings: 'Einstellungen schließen',
    ariaSoundToggle: 'Ton umschalten',
    ariaSettings: 'Einstellungen',
    ariaHome: 'Startseite',
    ariaNewPlan: 'Neuen Plan anlegen',
    ariaClearPlan: 'Plan entfernen',
    ariaEditPlan: 'Plan bearbeiten',
    ariaDeletePlan: 'Plan loschen',
    ariaMoveExerciseUp: 'Ubung nach oben verschieben',
    ariaMoveExerciseDown: 'Ubung nach unten verschieben',
    ariaAddExercise: 'Ubung hinzufugen',
    ariaSavePlan: 'Plan speichern',
    ariaShuffleExercises: 'Ubungen mischen',
    ariaSaveExerciseSelection: 'Ubungsauswahl speichern',
    ariaCancel: 'Abbrechen',
    ariaJoinDigit: 'Code-Ziffer {index}',
    exerciseFilterPlaceholder: 'Ubung suchen oder erstellen',
    exerciseCustomTitle: 'Eigene Ubung hinzufugen',
    exerciseCustomName: 'Ubungsname',
    exerciseCustomExists: 'Diese Ubung existiert bereits',
    exerciseCustomAdd: 'Hinzufugen',
    menuTemplates: 'Vorlagen',
    templateOverlayTitle: 'Vorlagen',
    templateBuiltIns: 'Voreinstellungen',
    templateUserSaved: 'Eigene Vorlagen',
    templateEmpty: 'Noch keine eigenen Vorlagen.',
    saveAsTemplate: 'Als Vorlage speichern',
    templateNamePlaceholder: 'Name der Vorlage',
    templateNameExists: 'Diese Vorlage existiert bereits',
    ariaDeleteTemplate: 'Vorlage loschen',
    sessionExpired: 'Session abgelaufen',
    ariaDismiss: 'Schließen',
    editorTrainingTitle: 'Training',
    templateButtonLabel: 'Vorlage'
  },
  en: {
    defaultSessionTitle: 'HIIT Session',
    invalidSession: 'Invalid session',
    offlineJoinDisabled: 'No internet connection',
    offlineStatus: 'Offline: no internet connection',
    join: 'Join',
    joinByCode: 'By code',
    joinBySearch: 'Search',
    noResults: 'No sessions found',
    searchPlaceholder: 'Session, focus or code',
    trainingCompleteA: 'Training complete',
    trainingCompleteB: 'Well done',
    audioUnlock: 'Enable sound',
    wakeLockUnlock: 'Keep screen awake',
    menuStartSession: 'Start session',
    menuJoinSession: 'Join session',
    menuAddDevice: 'Add device',
    menuPlans: 'Training plans',
    menuDeviceCode: 'Device',
    menuHome: 'Home',
    menuSettings: 'Settings',
    settingsLanguage: 'Language',
    settingsDesign: 'Design',
    languageGerman: 'Deutsch',
    languageEnglish: 'English',
    languageSpanish: 'Español',
    languagePortuguese: 'Português',
    themeDark: 'Dark',
    themeLight: 'Light',
    themeAuto: 'Auto',
    roundLabel: 'Round',
    setLabel: 'Set',
    restart: 'Restart',
    trainingLog: 'Training log',
    trainingLogEmpty: 'No trainings completed yet.',
    trainingLogClearAll: 'Clear all',
    trainingLogExercises: '{n} exercises',
    trainingLogDelete: 'Delete',
    planOverlayTitle: 'Training plans',
    planNewTitle: 'New plan',
    planEditTitle: 'Edit plan',
    planEmpty: 'No training plans saved yet.',
    planAddExercise: 'Add exercise',
    planSave: 'Save',
    planBack: 'Back',
    planNamePlaceholder: 'My plan',
    planNameRequired: 'Please give the training plan a name.',
    planSelectedLabel: 'Training plan',
    planAddButton: '+ Training plan',
    focusUpper: 'Upper body',
    focusLower: 'Lower body',
    focusCore: 'Core',
    focusFull: 'Full body',
    focusMix: 'Mixed',
    selected: 'Selected',
    select: 'Select',
    deviceCouple: 'Pair device',
    deviceAdd: 'Add',
    deviceCodePlaceholder: 'Code',
    deviceAddHint: '(add device)',
    deviceOwnIdError: 'You cannot add your own device ID',
    switchingOnline: 'Switching to online mode...',
    deviceAdded: 'Device {code} added',
    onlineUnavailable: 'Online unavailable',
    globalStart: 'Schedule global start',
    globalStartIn: 'Global start in {countdown}',
    start: 'Start',
    save: 'Save',
    warningTitle: 'Warning',
    exercisesCount: '{current}/{required} exercises',
    presetClassic63: 'Classic 6×3',
    presetCircuit210: 'Circuit 2×10',
    presetSuperset: 'Superset',
    presetIndividual: 'Custom',
    editorTitle: 'Editor',
    configRounds: 'Rounds',
    configSets: 'Sets',
    configActive: 'Active',
    configRest: 'Rest',
    configRoundRest: 'Round rest',
    configSuperset: 'Superset',
    configDynamicTraining: 'Dynamic training',
    dynamicNextExercise: 'Choose next exercise',
    controlPrev: 'Back',
    controlPlay: 'Play',
    controlPause: 'Pause',
    controlSkip: 'Next',
    ariaClose: 'Close',
    ariaCloseSettings: 'Close settings',
    ariaSoundToggle: 'Toggle sound',
    ariaSettings: 'Settings',
    ariaHome: 'Home',
    ariaNewPlan: 'Create new plan',
    ariaClearPlan: 'Clear plan',
    ariaEditPlan: 'Edit plan',
    ariaDeletePlan: 'Delete plan',
    ariaMoveExerciseUp: 'Move exercise up',
    ariaMoveExerciseDown: 'Move exercise down',
    ariaAddExercise: 'Add exercise',
    ariaSavePlan: 'Save plan',
    ariaShuffleExercises: 'Shuffle exercises',
    ariaSaveExerciseSelection: 'Save exercise selection',
    ariaCancel: 'Cancel',
    ariaJoinDigit: 'Code digit {index}',
    exerciseFilterPlaceholder: 'Find exercise or create',
    exerciseCustomTitle: 'Add custom exercise',
    exerciseCustomName: 'Exercise name',
    exerciseCustomExists: 'This exercise already exists',
    exerciseCustomAdd: 'Add',
    menuTemplates: 'Templates',
    templateOverlayTitle: 'Templates',
    templateBuiltIns: 'Built-in',
    templateUserSaved: 'My templates',
    templateEmpty: 'No saved templates yet.',
    saveAsTemplate: 'Save as template',
    templateNamePlaceholder: 'Template name',
    templateNameExists: 'A template with this name already exists',
    ariaDeleteTemplate: 'Delete template',
    sessionExpired: 'Session expired',
    ariaDismiss: 'Dismiss',
    editorTrainingTitle: 'Training',
    templateButtonLabel: 'Template'
  },
  es: {
    defaultSessionTitle: 'Sesión HIIT',
    invalidSession: 'Sesión no válida',
    offlineJoinDisabled: 'Sin conexión a internet',
    offlineStatus: 'Sin conexión a internet',
    join: 'Unirse',
    joinByCode: 'Por código',
    joinBySearch: 'Buscar',
    noResults: 'No se encontraron sesiones',
    searchPlaceholder: 'Sesión, enfoque o código',
    trainingCompleteA: 'Entrenamiento completado',
    trainingCompleteB: 'Buen trabajo',
    audioUnlock: 'Activar sonido',
    wakeLockUnlock: 'Mantener pantalla activa',
    menuStartSession: 'Iniciar sesión',
    menuJoinSession: 'Unirse a la sesión',
    menuAddDevice: 'Agregar dispositivo',
    menuPlans: 'Planes de entrenamiento',
    menuDeviceCode: 'Dispositivo',
    menuHome: 'Inicio',
    menuSettings: 'Ajustes',
    settingsLanguage: 'Idioma',
    settingsDesign: 'Diseño',
    languageGerman: 'Deutsch',
    languageEnglish: 'English',
    languageSpanish: 'Español',
    languagePortuguese: 'Português',
    themeDark: 'Oscuro',
    themeLight: 'Claro',
    themeAuto: 'Automático',
    roundLabel: 'Ronda',
    setLabel: 'Serie',
    restart: 'Reiniciar',
    trainingLog: 'Registro de entrenamientos',
    trainingLogEmpty: 'Aun no hay entrenamientos.',
    trainingLogClearAll: 'Borrar todo',
    trainingLogExercises: '{n} ejercicios',
    trainingLogDelete: 'Eliminar',
    planOverlayTitle: 'Planes de entrenamiento',
    planNewTitle: 'Nuevo plan',
    planEditTitle: 'Editar plan',
    planEmpty: 'Aún no hay planes guardados.',
    planAddExercise: 'Agregar ejercicio',
    planSave: 'Guardar',
    planBack: 'Volver',
    planNamePlaceholder: 'Mi plan',
    planNameRequired: 'Asigna un nombre al plan de entrenamiento.',
    planSelectedLabel: 'Plan de entrenamiento',
    planAddButton: '+ Plan de entrenamiento',
    focusUpper: 'Parte superior',
    focusLower: 'Parte inferior',
    focusCore: 'Core',
    focusFull: 'Cuerpo completo',
    focusMix: 'Mixto',
    selected: 'Seleccionado',
    select: 'Seleccionar',
    deviceCouple: 'Vincular dispositivo',
    deviceAdd: 'Agregar',
    deviceCodePlaceholder: 'Código',
    deviceAddHint: '(agregar dispositivo)',
    deviceOwnIdError: 'No puedes agregar tu propio ID de dispositivo',
    switchingOnline: 'Cambiando al modo en línea...',
    deviceAdded: 'Dispositivo {code} agregado',
    onlineUnavailable: 'En línea no disponible',
    globalStart: 'Programar inicio global',
    globalStartIn: 'Inicio global en {countdown}',
    start: 'Iniciar',
    save: 'Guardar',
    warningTitle: 'Atención',
    exercisesCount: '{current}/{required} ejercicios',
    presetClassic63: 'Classic 6×3',
    presetCircuit210: 'Circuit 2×10',
    presetSuperset: 'Superserie',
    presetIndividual: 'Personalizado',
    editorTitle: 'Editor',
    configRounds: 'Rondas',
    configSets: 'Series',
    configActive: 'Activo',
    configRest: 'Pausa',
    configRoundRest: 'Pausa entre rondas',
    configSuperset: 'Superserie',
    configDynamicTraining: 'Entrenamiento dinámico',
    dynamicNextExercise: 'Elegir siguiente ejercicio',
    controlPrev: 'Atrás',
    controlPlay: 'Iniciar',
    controlPause: 'Pausar',
    controlSkip: 'Siguiente',
    ariaClose: 'Cerrar',
    ariaCloseSettings: 'Cerrar ajustes',
    ariaSoundToggle: 'Alternar sonido',
    ariaSettings: 'Ajustes',
    ariaHome: 'Inicio',
    ariaNewPlan: 'Crear nuevo plan',
    ariaClearPlan: 'Quitar plan',
    ariaEditPlan: 'Editar plan',
    ariaDeletePlan: 'Eliminar plan',
    ariaMoveExerciseUp: 'Mover ejercicio hacia arriba',
    ariaMoveExerciseDown: 'Mover ejercicio hacia abajo',
    ariaAddExercise: 'Agregar ejercicio',
    ariaSavePlan: 'Guardar plan',
    ariaShuffleExercises: 'Mezclar ejercicios',
    ariaSaveExerciseSelection: 'Guardar selección de ejercicios',
    ariaCancel: 'Cancelar',
    ariaJoinDigit: 'Dígito del código {index}',
    exerciseFilterPlaceholder: 'Buscar ejercicio o crear',
    exerciseCustomTitle: 'Agregar ejercicio personalizado',
    exerciseCustomName: 'Nombre del ejercicio',
    exerciseCustomExists: 'Este ejercicio ya existe',
    exerciseCustomAdd: 'Agregar',
    menuTemplates: 'Plantillas',
    templateOverlayTitle: 'Plantillas',
    templateBuiltIns: 'Predefinidas',
    templateUserSaved: 'Mis plantillas',
    templateEmpty: 'Aún no hay plantillas guardadas.',
    saveAsTemplate: 'Guardar como plantilla',
    templateNamePlaceholder: 'Nombre de la plantilla',
    templateNameExists: 'Ya existe una plantilla con ese nombre',
    ariaDeleteTemplate: 'Eliminar plantilla',
    sessionExpired: 'Sesión expirada',
    ariaDismiss: 'Cerrar',
    editorTrainingTitle: 'Entrenamiento',
    templateButtonLabel: 'Plantilla'
  },
  pt: {
    defaultSessionTitle: 'Sessão HIIT',
    invalidSession: 'Sessão inválida',
    offlineJoinDisabled: 'Sem conexão com a internet',
    offlineStatus: 'Offline: sem conexão com a internet',
    join: 'Entrar',
    joinByCode: 'Por código',
    joinBySearch: 'Pesquisar',
    noResults: 'Nenhuma sessão encontrada',
    searchPlaceholder: 'Sessão, foco ou código',
    trainingCompleteA: 'Treino concluído',
    trainingCompleteB: 'Bom trabalho',
    audioUnlock: 'Ativar som',
    wakeLockUnlock: 'Manter tela ativa',
    menuStartSession: 'Iniciar sessão',
    menuJoinSession: 'Entrar na sessão',
    menuAddDevice: 'Adicionar dispositivo',
    menuPlans: 'Planos de treino',
    menuDeviceCode: 'Dispositivo',
    menuHome: 'Início',
    menuSettings: 'Configurações',
    settingsLanguage: 'Idioma',
    settingsDesign: 'Design',
    languageGerman: 'Deutsch',
    languageEnglish: 'English',
    languageSpanish: 'Español',
    languagePortuguese: 'Português',
    themeDark: 'Escuro',
    themeLight: 'Claro',
    themeAuto: 'Automático',
    roundLabel: 'Rodada',
    setLabel: 'Série',
    restart: 'Reiniciar',
    trainingLog: 'Registro de treinos',
    trainingLogEmpty: 'Nenhum treino concluido.',
    trainingLogClearAll: 'Apagar tudo',
    trainingLogExercises: '{n} exercicios',
    trainingLogDelete: 'Apagar',
    planOverlayTitle: 'Planos de treino',
    planNewTitle: 'Novo plano',
    planEditTitle: 'Editar plano',
    planEmpty: 'Nenhum plano salvo ainda.',
    planAddExercise: 'Adicionar exercício',
    planSave: 'Salvar',
    planBack: 'Voltar',
    planNamePlaceholder: 'Meu plano',
    planNameRequired: 'Dê um nome ao plano de treino.',
    planSelectedLabel: 'Plano de treino',
    planAddButton: '+ Plano de treino',
    focusUpper: 'Parte superior',
    focusLower: 'Parte inferior',
    focusCore: 'Core',
    focusFull: 'Corpo inteiro',
    focusMix: 'Misto',
    selected: 'Selecionado',
    select: 'Selecionar',
    deviceCouple: 'Vincular dispositivo',
    deviceAdd: 'Adicionar',
    deviceCodePlaceholder: 'Código',
    deviceAddHint: '(adicionar dispositivo)',
    deviceOwnIdError: 'Voce nao pode adicionar seu proprio ID de dispositivo',
    switchingOnline: 'Mudando para o modo online...',
    deviceAdded: 'Dispositivo {code} adicionado',
    onlineUnavailable: 'Online indisponível',
    globalStart: 'Agendar início global',
    globalStartIn: 'Início global em {countdown}',
    start: 'Iniciar',
    save: 'Salvar',
    warningTitle: 'Atenção',
    exercisesCount: '{current}/{required} exercícios',
    presetClassic63: 'Classic 6×3',
    presetCircuit210: 'Circuit 2×10',
    presetSuperset: 'Supersérie',
    presetIndividual: 'Personalizado',
    editorTitle: 'Editor',
    configRounds: 'Rodadas',
    configSets: 'Séries',
    configActive: 'Ativo',
    configRest: 'Pausa',
    configRoundRest: 'Pausa entre rodadas',
    configSuperset: 'Supersérie',
    configDynamicTraining: 'Treino dinâmico',
    dynamicNextExercise: 'Escolher próximo exercício',
    controlPrev: 'Voltar',
    controlPlay: 'Iniciar',
    controlPause: 'Pausar',
    controlSkip: 'Próximo',
    ariaClose: 'Fechar',
    ariaCloseSettings: 'Fechar configurações',
    ariaSoundToggle: 'Alternar som',
    ariaSettings: 'Configurações',
    ariaHome: 'Início',
    ariaNewPlan: 'Criar novo plano',
    ariaClearPlan: 'Remover plano',
    ariaEditPlan: 'Editar plano',
    ariaDeletePlan: 'Excluir plano',
    ariaMoveExerciseUp: 'Mover exercício para cima',
    ariaMoveExerciseDown: 'Mover exercício para baixo',
    ariaAddExercise: 'Adicionar exercício',
    ariaSavePlan: 'Salvar plano',
    ariaShuffleExercises: 'Embaralhar exercícios',
    ariaSaveExerciseSelection: 'Salvar seleção de exercícios',
    ariaCancel: 'Cancelar',
    ariaJoinDigit: 'Dígito do código {index}',
    exerciseFilterPlaceholder: 'Buscar exercicio ou criar',
    exerciseCustomTitle: 'Adicionar exercicio personalizado',
    exerciseCustomName: 'Nome do exercicio',
    exerciseCustomExists: 'Este exercicio ja existe',
    exerciseCustomAdd: 'Adicionar',
    menuTemplates: 'Modelos',
    templateOverlayTitle: 'Modelos',
    templateBuiltIns: 'Predefinidos',
    templateUserSaved: 'Meus modelos',
    templateEmpty: 'Ainda não há modelos salvos.',
    saveAsTemplate: 'Salvar como modelo',
    templateNamePlaceholder: 'Nome do modelo',
    templateNameExists: 'Já existe um modelo com este nome',
    ariaDeleteTemplate: 'Excluir modelo',
    sessionExpired: 'Sessão expirada',
    ariaDismiss: 'Fechar',
    editorTrainingTitle: 'Treino',
    templateButtonLabel: 'Modelo'
  }
};

const SUPPORTED_LANGUAGES = ['de', 'en', 'es', 'pt'];

function getTranslation(language, key) {
  if (key === 'account_loginError') key = 'loginFailed';
  return ACCOUNT_TRANSLATIONS[language]?.[key] || TRANSLATIONS[language]?.[key] || TRANSLATIONS.de[key] || key;
}

function interpolate(template, values = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_match, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : `{${key}}`
  ));
}

function getDefaultSessionTitle(language) {
  return getTranslation(language, 'defaultSessionTitle');
}

function isDefaultSessionTitle(title) {
  const normalizedTitle = String(title || '').trim().toLowerCase();
  return SUPPORTED_LANGUAGES.some((language) => (
    getDefaultSessionTitle(language).trim().toLowerCase() === normalizedTitle
  ));
}

const PRESETS = {
  classic63: {
    name: 'classic63',
    rounds: 6,
    sets: 3,
    activeMs: 40_000,
    restMs: 25_000,
    roundRestMs: 60_000,
    supersetEnabled: false,
    dynamicTraining: false
  },
  circuit210: {
    name: 'circuit210',
    rounds: 2,
    sets: 10,
    activeMs: 50_000,
    restMs: 20_000,
    roundRestMs: 20_000,
    supersetEnabled: false,
    dynamicTraining: false
  },
  superset: {
    name: 'superset',
    rounds: 5,
    sets: 3,
    activeMs: 60_000,
    restMs: 20_000,
    roundRestMs: 60_000,
    supersetEnabled: true,
    dynamicTraining: false
  }
};

function normalizeSessionConfig(config = {}) {
  return {
    ...PRESETS.classic63,
    ...config
  };
}

function normalizeSnapshot(snapshot) {
  if (!snapshot) return snapshot;
  return {
    ...snapshot,
    config: normalizeSessionConfig(snapshot.config || {})
  };
}

function inferTrainingPlanSelected(title, exercises) {
  return Array.isArray(exercises) && exercises.length > 0 && !isDefaultSessionTitle(title);
}

function formatMs(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60).toString();
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function createIdleSnapshot(sessionId, config) {
  return {
    sessionId,
    config: normalizeSessionConfig(config),
    state: {
      phase: 'idle',
      currentRound: 1,
      currentSet: 1,
      segmentInSet: 1,
      remainingMs: 0,
      running: false,
      blockDurationMs: 0,
      blockStartedAt: null,
      updatedAt: Date.now()
    }
  };
}

function getSupersetDurations(totalActiveMs) {
  const midPauseMs = 5000;
  if (totalActiveMs <= 2000) {
    const firstHalfMs = Math.max(1000, Math.floor(totalActiveMs / 2));
    const secondHalfMs = Math.max(1000, totalActiveMs - firstHalfMs);
    return { firstHalfMs, secondHalfMs, midPauseMs: 0 };
  }

  const firstHalfMs = Math.floor(totalActiveMs / 2);
  const secondHalfMs = totalActiveMs - firstHalfMs;
  return { firstHalfMs, secondHalfMs, midPauseMs };
}

function startOfflineActive(snapshot, segmentInSet = 1) {
  const config = snapshot?.config || {};
  const state = snapshot?.state || {};
  const planExercises = snapshot?.plan?.exercises || [];
  const mode = getPerExerciseMode(planExercises, config, state, segmentInSet);
  const timing = getActiveBlockTiming(config, mode, segmentInSet);
  return {
    ...snapshot,
    state: {
      ...state,
      phase: 'active',
      segmentInSet: timing.segmentInSet,
      running: true,
      blockDurationMs: timing.durationMs,
      blockStartedAt: Date.now(),
      remainingMs: timing.durationMs,
      updatedAt: Date.now()
    }
  };
}

function startOfflineCountdown(state) {
  return {
    ...state,
    phase: 'countdown',
    segmentInSet: 1,
    running: true,
    blockDurationMs: START_COUNTDOWN_MS,
    blockStartedAt: Date.now(),
    remainingMs: START_COUNTDOWN_MS,
    updatedAt: Date.now()
  };
}

function getRemainingMs(state) {
  if (!state) return 0;
  if (state.running && state.blockStartedAt && state.blockDurationMs) {
    const elapsed = Date.now() - state.blockStartedAt;
    return Math.max(0, Math.min(state.blockDurationMs, state.blockDurationMs - elapsed));
  }
  return Math.max(0, state.remainingMs || 0);
}

function advanceOffline(snapshot) {
  if (!snapshot) return snapshot;
  const config = snapshot.config;
  const state = snapshot.state;
  const planExercises = snapshot?.plan?.exercises || [];

  if (state.phase === 'active') {
    const mode = getPerExerciseMode(planExercises, config, state, state.segmentInSet || 1);
    const timing = getActiveBlockTiming(config, mode, state.segmentInSet || 1);

    if (timing.isSuperset && (state.segmentInSet || 1) === 1) {
      const { midPauseMs } = timing;
      if (midPauseMs > 0) {
        return {
          ...snapshot,
          state: {
            ...state,
            phase: 'supersetPause',
            running: true,
            blockDurationMs: midPauseMs,
            blockStartedAt: Date.now(),
            remainingMs: midPauseMs,
            updatedAt: Date.now()
          }
        };
      }

      return startOfflineActive(snapshot, 2);
    }

    if (state.currentSet < config.sets) {
      const restDuration = getRestBlockDuration(config, mode);
      return {
        ...snapshot,
        state: {
          ...state,
          currentSet: state.currentSet + 1,
          phase: 'rest',
          segmentInSet: 1,
          running: true,
          blockDurationMs: restDuration,
          blockStartedAt: Date.now(),
          remainingMs: restDuration,
          updatedAt: Date.now()
        }
      };
    }
    if (state.currentRound < config.rounds) {
      return {
        ...snapshot,
        state: {
          ...state,
          currentRound: state.currentRound + 1,
          currentSet: 1,
          phase: 'roundRest',
          running: true,
          blockDurationMs: config.roundRestMs,
          blockStartedAt: Date.now(),
          remainingMs: config.roundRestMs,
          updatedAt: Date.now()
        }
      };
    }
    return {
      ...snapshot,
      state: {
        ...state,
        phase: 'done',
        segmentInSet: 1,
        running: false,
        remainingMs: 0,
        blockStartedAt: null,
        blockDurationMs: 0,
        updatedAt: Date.now()
      }
    };
  }

  if (state.phase === 'supersetPause') {
    return startOfflineActive(snapshot, 2);
  }

  if (state.phase === 'countdown') {
    return startOfflineActive(snapshot, 1);
  }

  if (state.phase === 'rest') {
    return startOfflineActive(snapshot, 1);
  }

  if (state.phase === 'roundRest') {
    return startOfflineActive(snapshot, 1);
  }

  return snapshot;
}

function pauseOfflineSnapshot(snapshot) {
  if (!snapshot?.state) return snapshot;
  const state = snapshot.state;
  const durationMs = state.phase === 'idle' || state.phase === 'done'
    ? 0
    : Math.max(0, state.blockDurationMs || state.remainingMs || 0);

  return {
    ...snapshot,
    state: {
      ...state,
      running: false,
      blockStartedAt: null,
      blockDurationMs: durationMs,
      remainingMs: durationMs,
      updatedAt: Date.now()
    }
  };
}

function rewindOffline(snapshot, pauseAfter = false) {
  if (!snapshot?.state) return snapshot;

  const cfg = snapshot.config;
  const st = snapshot.state;

  if (st.phase === 'idle' || st.phase === 'done') {
    return {
      ...snapshot,
      state: stepPreviewState(cfg, st, -1, snapshot.plan?.exercises?.length || 0)
    };
  }

  if (pauseAfter && st.phase === 'countdown') {
    return {
      ...snapshot,
      state: createPreviewStateForSlot(cfg, st, getProgressPreviewBlockIndex(cfg, st), 'idle')
    };
  }

  let blockDuration;
  if (st.phase === 'active') {
    const planExercises = snapshot.plan?.exercises || [];
    const mode = getPerExerciseMode(planExercises, cfg, st, st.segmentInSet || 1);
    blockDuration = getActiveBlockTiming(cfg, mode, st.segmentInSet || 1).durationMs;
  } else if (st.phase === 'rest') {
    const planExercises = snapshot.plan?.exercises || [];
    const mode = getPerExerciseMode(planExercises, cfg, st, 1);
    blockDuration = getRestBlockDuration(cfg, mode);
  } else if (st.phase === 'roundRest') {
    blockDuration = Number(cfg.roundRestMs) || 90000;
  } else if (st.phase === 'supersetPause') {
    blockDuration = 5000;
  } else if (st.phase === 'countdown') {
    blockDuration = START_COUNTDOWN_MS;
  } else {
    blockDuration = st.blockDurationMs || Number(cfg.activeMs) || 30000;
  }

  const currentRemaining = pauseAfter ? blockDuration : getRemainingMs(st);
  const elapsed = blockDuration - currentRemaining;
  let nextSnapshot = null;

  if (elapsed < 2000 && st.phase !== 'countdown') {
    const planEx = snapshot.plan?.exercises || [];
    if (st.phase === 'supersetPause') {
      nextSnapshot = startOfflineActive(snapshot, 1);
    } else if (st.phase === 'active' && (st.segmentInSet || 1) === 2) {
      nextSnapshot = startOfflineActive(snapshot, 1);
    } else if (st.phase === 'active' && (st.segmentInSet || 1) === 1) {
      if (st.currentSet > 1) {
        const previousState = { ...st, currentSet: st.currentSet - 1 };
        const previousMode = getPerExerciseMode(planEx, cfg, previousState, 1);
        const restDur = getRestBlockDuration(cfg, previousMode);
        nextSnapshot = {
          ...snapshot,
          state: {
            ...st,
            currentSet: st.currentSet - 1,
            phase: 'rest',
            running: true,
            blockStartedAt: Date.now(),
            blockDurationMs: restDur,
            remainingMs: restDur,
            segmentInSet: 1,
            updatedAt: Date.now()
          }
        };
      } else if (st.currentRound > 1) {
        const rrDur = Number(cfg.roundRestMs) || 90000;
        nextSnapshot = {
          ...snapshot,
          state: {
            ...st,
            currentRound: st.currentRound - 1,
            currentSet: Math.max(1, Number(cfg.sets) || 1),
            phase: 'roundRest',
            running: true,
            blockStartedAt: Date.now(),
            blockDurationMs: rrDur,
            remainingMs: rrDur,
            segmentInSet: 1,
            updatedAt: Date.now()
          }
        };
      }
    } else if (st.phase === 'rest') {
      const mode = getPerExerciseMode(planEx, cfg, st, 1);
      const isSupersetLike = mode === 'SZ' || mode === 'LR';
      nextSnapshot = startOfflineActive(snapshot, isSupersetLike ? 2 : 1);
    } else if (st.phase === 'roundRest') {
      const mode = getPerExerciseMode(planEx, cfg, st, 1);
      const isSupersetLike = mode === 'SZ' || mode === 'LR';
      nextSnapshot = startOfflineActive(snapshot, isSupersetLike ? 2 : 1);
    }
  }

  if (!nextSnapshot) {
    nextSnapshot = {
      ...snapshot,
      state: {
        ...st,
        running: true,
        blockStartedAt: Date.now(),
        blockDurationMs: blockDuration,
        remainingMs: blockDuration,
        updatedAt: Date.now()
      }
    };
  }

  return pauseAfter ? pauseOfflineSnapshot(nextSnapshot) : nextSnapshot;
}

function advanceOfflinePreview(snapshot) {
  if (!snapshot?.state) return snapshot;

  const st = snapshot.state;
  if (st.phase === 'idle' || st.phase === 'done') {
    return {
      ...snapshot,
      state: stepPreviewState(snapshot.config, st, 1, snapshot.plan?.exercises?.length || 0)
    };
  }

  const nextSnapshot = advanceOffline({
    ...snapshot,
    state: {
      ...st,
      running: true,
      remainingMs: 0,
      blockDurationMs: Math.max(st.blockDurationMs || 0, getRemainingMs(st)),
      blockStartedAt: Date.now(),
      updatedAt: Date.now()
    }
  });

  return pauseOfflineSnapshot(nextSnapshot);
}

function jumpLastOffline(snapshot, pauseAfter = false) {
  if (!snapshot?.state) return snapshot;

  const cfg = snapshot.config;
  const st = snapshot.state;

  if (st.phase === 'idle' || st.phase === 'done') {
    return {
      ...snapshot,
      state: createPreviewStateForSlot(cfg, st, getConfiguredPreviewBlockCount(cfg, snapshot.plan?.exercises?.length || 0) - 1, st.phase === 'done' ? 'done' : 'idle')
    };
  }

  const lastRound = Math.max(1, Number(cfg.rounds) || 1);
  const lastSet = Math.max(1, Number(cfg.sets) || 1);
  const nextSnapshot = startOfflineActive({
    ...snapshot,
    state: {
      ...st,
      currentRound: lastRound,
      currentSet: lastSet,
      segmentInSet: 1
    }
  }, 1);

  return pauseAfter ? pauseOfflineSnapshot(nextSnapshot) : nextSnapshot;
}

function stepOfflineControlSnapshot(snapshot, action) {
  if (!snapshot?.state) return snapshot;

  const cfg = snapshot.config;
  const st = snapshot.state;

  if (action === 'play') {
    if (st.phase === 'idle' || st.phase === 'done') {
      return {
        ...snapshot,
        state: {
          ...startOfflineCountdown(st)
        }
      };
    }
    return {
      ...snapshot,
      state: {
        ...st,
        running: true,
        segmentInSet: st.segmentInSet || 1,
        blockStartedAt: Date.now() - ((st.blockDurationMs || st.remainingMs) - st.remainingMs),
        updatedAt: Date.now()
      }
    };
  }

  if (action === 'pause') {
    const remaining = getRemainingMs(st);
    return {
      ...snapshot,
      state: {
        ...st,
        running: false,
        remainingMs: remaining,
        blockStartedAt: null,
        updatedAt: Date.now()
      }
    };
  }

  if (action === 'reset') {
    return createIdleSnapshot(snapshot.sessionId, cfg);
  }

  if (action === 'prev') {
    return rewindOffline(snapshot, !st.running);
  }

  if (action === 'skip') {
    if (!st.running) {
      return advanceOfflinePreview(snapshot);
    }
    return advanceOffline({
      ...snapshot,
      state: {
        ...st,
        running: true,
        remainingMs: 0,
        blockDurationMs: 0,
        blockStartedAt: Date.now(),
        updatedAt: Date.now()
      }
    });
  }

  if (action === 'jumpLast') {
    return jumpLastOffline(snapshot, !st.running);
  }

  return snapshot;
}

function App() {
  const [language, setLanguage] = useState(() => {
    const storedLanguage = localStorage.getItem(LANGUAGE_KEY);
    if (SUPPORTED_LANGUAGES.includes(storedLanguage)) {
      return storedLanguage;
    }
    const locale = (navigator.language || 'de').slice(0, 2).toLowerCase();
    return SUPPORTED_LANGUAGES.includes(locale) ? locale : 'de';
  });
  const t = useCallback((key, values = {}) => interpolate(getTranslation(language, key), values), [language]);
  const [completionMessageIndex, setCompletionMessageIndex] = useState(0);
  const [audioReady, setAudioReady] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [wakeFallbackActive, setWakeFallbackActive] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    const stored = localStorage.getItem(THEME_MODE_KEY);
    return ['auto', 'dark', 'light'].includes(stored) ? stored : 'auto';
  });
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deviceOverlayOpen, setDeviceOverlayOpen] = useState(false);
  const [configEditorOpen, setConfigEditorOpen] = useState(false);
  const [joinOverlayOpen, setJoinOverlayOpen] = useState(false);
  const [planOverlayOpen, setPlanOverlayOpen] = useState(false);
  const [exercisePickerOpen, setExercisePickerOpen] = useState(false);
  const [exercisePickerMode, setExercisePickerMode] = useState('plan');
  const [exerciseFocusFilters, setExerciseFocusFilters] = useState([]);
  const [customExerciseFocus, setCustomExerciseFocus] = useState('full');
  const [exercisePickerSelection, setExercisePickerSelection] = useState([]);
  const [exerciseFilterInput, setExerciseFilterInput] = useState('');
  const [customExerciseInput, setCustomExerciseInput] = useState('');
  const [customExerciseModalOpen, setCustomExerciseModalOpen] = useState(false);
  const [customExerciseError, setCustomExerciseError] = useState('');
  const [planEditMode, setPlanEditMode] = useState('list');
  const [editingPlanIndex, setEditingPlanIndex] = useState(null);
  const [scheduleOverlayOpen, setScheduleOverlayOpen] = useState(false);
  const [planCountWarningOpen, setPlanCountWarningOpen] = useState(false);
  const [sessionExpiredVisible, setSessionExpiredVisible] = useState(false);
  const [templateOverlayOpen, setTemplateOverlayOpen] = useState(false);
  const [templateSavePromptOpen, setTemplateSavePromptOpen] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [templateNameError, setTemplateNameError] = useState('');
  const [templateLibrary, setTemplateLibrary] = useState(() => {
    try {
      const raw = localStorage.getItem(TEMPLATE_LIBRARY_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  });
  const [trainingLog, setTrainingLog] = useState(() => {
    try {
      const raw = localStorage.getItem(TRAINING_LOG_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [trainingLogOpen, setTrainingLogOpen] = useState(false);
  const hasLoggedDoneRef = useRef(false);
  const [sessionTitle, setSessionTitle] = useState(() => getDefaultSessionTitle(language));
  const [planFocus, setPlanFocus] = useState('full');
  const [planExercises, setPlanExercises] = useState([]);
  const [trainingPlanSelected, setTrainingPlanSelected] = useState(false);
  const [selectedDynamicSlots, setSelectedDynamicSlots] = useState({});
  const [activePlanLibraryIndex, setActivePlanLibraryIndex] = useState(null);
  const [planTitleInput, setPlanTitleInput] = useState('');
  const [planTitleError, setPlanTitleError] = useState('');
  const [planLibrary, setPlanLibrary] = useState(() => {
    try {
      const raw = localStorage.getItem(PLAN_LIBRARY_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  });
  const [customExercises, setCustomExercises] = useState(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_EXERCISES_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? normalizeCustomExercises(parsed) : [];
    } catch (_error) {
      return [];
    }
  });
  const [librariesReady, setLibrariesReady] = useState(!IS_NATIVE);
  const account = useAccountSync(API_BASE.replace(/\/$/, ''), {
    plans: planLibrary, trainingLog, customExercises, templates: templateLibrary
  }, { plans: setPlanLibrary, trainingLog: setTrainingLog, customExercises: setCustomExercises, templates: setTemplateLibrary }, librariesReady);
  const [shareDialog, setShareDialog] = useState(null);
  const [shareError, setShareError] = useState('');
  const [sharingPlan, setSharingPlan] = useState(false);
  const sharePlan = async (plan) => {
    setShareError('');
    setSharingPlan(true);
    try {
      const result = await account.request('/api/shared-plans', { method: 'POST', body: JSON.stringify({ plan }) });
      setShareDialog(result);
    } catch (_error) { setShareError(t('shareFailed')); }
    finally { setSharingPlan(false); }
  };
  const [scheduledStartInput, setScheduledStartInput] = useState('');
  const [view, setView] = useState('session');
  const [configEditorMode, setConfigEditorMode] = useState('classic63');
  const [offlineMode, setOfflineMode] = useState(false);
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  });
  const [isController, setIsController] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [joinDigits, setJoinDigits] = useState(['', '', '', '']);
  const [joinError, setJoinError] = useState('');
  const [deviceCodeInput, setDeviceCodeInput] = useState('');
  const [linkMessage, setLinkMessage] = useState('');
  const [configForm, setConfigForm] = useState(PRESETS.classic63);
  const [configEditorForm, setConfigEditorForm] = useState(PRESETS.classic63);
  const [, setRenderTick] = useState(Date.now());
  const [lastIndividual, setLastIndividual] = useState(() => {
    try {
      const raw = localStorage.getItem(LAST_INDIVIDUAL_KEY);
      if (!raw) return { ...PRESETS.classic63, name: 'individual' };
      return { ...PRESETS.classic63, ...JSON.parse(raw), name: 'individual' };
    } catch (_error) {
      return { ...PRESETS.classic63, name: 'individual' };
    }
  });

  const wsRef = useRef(null);
  const wsReconnectTimerRef = useRef(null);
  const wsReconnectAttemptRef = useRef(0);
  const wsManualCloseRef = useRef(false);
  const wakeLockRef = useRef(null);
  const noSleepRef = useRef(null);
  const noSleepEnabledRef = useRef(false);
  const wakeFallbackPendingRef = useRef(false);
  const joinInputRefs = useRef([]);
  const audioContextRef = useRef(null);
  const playedCueIdsRef = useRef(new Set());
  const prevTapAtRef = useRef(0);
  const autoOpenedDynamicPromptRef = useRef('');
  const snapshotRef = useRef(null);
  const syncDirtyRef = useRef(false);
  const apiBase = useMemo(() => API_BASE.replace(/\/$/, ''), []);
  const state = snapshot?.state;
  const shouldKeepScreenAwake = Boolean(state?.running || snapshot?.scheduledStartAt);
  const supportsScreenWakeLock = typeof navigator !== 'undefined'
    && typeof navigator.wakeLock?.request === 'function';
  const config = normalizeSessionConfig(snapshot?.config || configForm);
  const remainingMs = getRemainingMs(state);
  const phaseDurationMs = Math.max(0, state?.blockDurationMs || 0);
  const phaseProgressRatio = phaseDurationMs > 0 ? Math.min(1, Math.max(0, 1 - (remainingMs / phaseDurationMs))) : 0;
  const phaseProgressDeg = `${Math.round(phaseProgressRatio * 360)}deg`;
  const timerProgressColor = state?.phase === 'active'
    ? ACTIVE_GREEN
    : (state?.phase === 'rest' || state?.phase === 'roundRest')
      ? '#FF4500'
      : '#3b82f6';
  const planButtonLabel = planExercises.length
    ? (sessionTitle && !isDefaultSessionTitle(sessionTitle) ? sessionTitle : t('planSelectedLabel'))
    : t('planAddButton');

  const scheduleTargetMs = snapshot?.scheduledStartAt ? Date.parse(snapshot.scheduledStartAt) : NaN;
  const scheduleRemainingMs = Number.isFinite(scheduleTargetMs) ? Math.max(0, scheduleTargetMs - Date.now()) : 0;
  const scheduleTotalSeconds = Math.floor(scheduleRemainingMs / 1000);
  const scheduleCountdown = `${Math.floor(scheduleTotalSeconds / 60)}:${(scheduleTotalSeconds % 60).toString().padStart(2, '0')}`;
  const planExerciseSlots = useMemo(() => flattenPlanExercises(planExercises), [planExercises]);
  const hasSelectedTrainingPlan = trainingPlanSelected;
  const currentProgressSlotIndex = useMemo(() => getProgressPlanSlotIndex(config, state), [config, state]);
  const currentPlanExerciseSlotIndex = useMemo(() => {
    return getCurrentPlanSlotIndex(config, state, planExercises.length);
  }, [config, planExercises.length, state]);
  const serverPersistenceEnabled = !offlineMode && isOnline;

  const derivedFocus = useMemo(() => {
    if (!planExerciseSlots.length) return 'full';
    const categories = new Set(planExerciseSlots.map((ex) => getExerciseFocus(ex, customExercises)));
    if (categories.size === 0) return 'full';
    if (categories.size === 1) return [...categories][0];
    return 'mix';
  }, [planExerciseSlots, customExercises]);

  const currentPlanExercise = useMemo(() => {
    if (!planExercises.length) return null;
    if (currentPlanExerciseSlotIndex < 0) return null;

    const current = planExercises[currentPlanExerciseSlotIndex] || null;
    if (!current) return null;

    const mode = normalizeExerciseMode(current.mode);
    const isSupersetPause = state?.phase === 'supersetPause';
    const effectiveSegment = isSupersetPause ? 2 : (state?.segmentInSet || 1);
    if (mode === 'SZ' && effectiveSegment === 2) {
      if (current.supersetExercise?.name) return current.supersetExercise;
      return current;
    }

    return current;
  }, [currentPlanExerciseSlotIndex, planExercises, state]);

  const currentPlanExerciseIndex = useMemo(() => {
    if (!planExercises.length) return -1;
    if (currentPlanExerciseSlotIndex < 0) return -1;
    const current = planExercises[currentPlanExerciseSlotIndex] || null;
    if (!current) return currentPlanExerciseSlotIndex;
    const mode = normalizeExerciseMode(current.mode);
    if (mode === 'SZ' && (state?.segmentInSet || 1) === 2) {
      if (current.supersetExercise?.name) return currentPlanExerciseSlotIndex;
      return currentPlanExerciseSlotIndex;
    }
    return currentPlanExerciseSlotIndex;
  }, [currentPlanExerciseSlotIndex, planExercises, state]);

  const currentExerciseMode = useMemo(() => {
    return getPerExerciseMode(planExercises, config, state, state?.segmentInSet || 1);
  }, [config, planExercises, state]);

  const showSupersetBadge = useMemo(() => {
    if (!state) return false;
    if (state.phase === 'idle' || state.phase === 'done' || state.phase === 'countdown') return false;
    if (currentExerciseMode === 'SZ' || currentExerciseMode === 'LR') return true;
    if (state.phase !== 'active' && state.phase !== 'supersetPause') return false;
    return Boolean(config?.supersetEnabled);
  }, [config?.supersetEnabled, currentExerciseMode, state]);

  const canPickDynamicExercise = Boolean(isController)
    && Boolean(config?.dynamicTraining)
    && (state?.phase === 'idle' || state?.phase === 'rest' || state?.phase === 'roundRest');
  const needsDynamicSelectionBeforeStart = Boolean(isController)
    && Boolean(config?.dynamicTraining)
    && state?.phase === 'idle'
    && !selectedDynamicSlots[`slot-${currentProgressSlotIndex}`];
  const dynamicSelectionPromptKey = canPickDynamicExercise
    ? `${state?.phase || 'idle'}-${currentProgressSlotIndex}-${state?.currentRound || 0}-${state?.currentSet || 0}-${state?.updatedAt || 0}`
    : '';

  const requiredExerciseCount = useMemo(() => {
    const rounds = Math.max(1, Number(configEditorForm?.rounds) || 1);
    const sets = Math.max(1, Number(configEditorForm?.sets) || 1);
    if (configEditorMode === 'circuit210') return sets;
    if (configEditorMode === 'classic63') return rounds;
    if (configEditorMode === 'superset') return rounds;
    return Math.max(rounds, sets);
  }, [configEditorForm?.rounds, configEditorForm?.sets, configEditorMode]);

  const focusMeta = useMemo(() => ({
    upper: { emoji: '💪', label: t('focusUpper') },
    lower: { emoji: '🦵', label: t('focusLower') },
    core: { emoji: '❤️', label: t('focusCore') },
    full: { emoji: '🧍', label: t('focusFull') },
    mix: { emoji: '📼', label: t('focusMix') }
  }), [t]);

  const builtInTemplates = useMemo(() => ([
    { id: 'classic63', title: t('presetClassic63'), builtIn: true, config: PRESETS.classic63 },
    { id: 'circuit210', title: t('presetCircuit210'), builtIn: true, config: PRESETS.circuit210 },
    { id: 'superset', title: t('presetSuperset'), builtIn: true, config: PRESETS.superset }
  ]), [t]);

  const exercisePickerOptions = useMemo(() => {
    const all = [...Object.values(EXERCISE_LIBRARY).flat(), ...customExercises.map((entry) => entry.name), ...planExerciseSlots.map((entry) => entry.name)];
    const deduped = [];
    const seen = new Set();
    for (const name of all) {
      const normalized = normalizeExerciseName(name);
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) continue;
      seen.add(key);
      deduped.push(normalized);
    }
    return deduped.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [customExercises, planExerciseSlots]);

  const exerciseFocus = useCallback((name) => getExerciseFocus(
    planExerciseSlots.find((entry) => isSameExerciseName(entry.name, name)) || name, customExercises
  ), [planExerciseSlots, customExercises]);
  const filteredExerciseOptions = useMemo(() => searchExercises(
    exercisePickerOptions, exerciseFilterInput, exerciseFocusFilters, exerciseFocus
  ), [exercisePickerOptions, exerciseFilterInput, exerciseFocusFilters, exerciseFocus]);

  const formatExerciseLabel = useCallback((name) => {
    const normalized = normalizeExerciseName(name);
    if (!normalized) return '';
    if (language === 'en') return normalized;
    if (!['de', 'es', 'pt'].includes(language)) return normalized;
    const english = EXERCISE_EN_EQUIVALENTS[normalized.toLowerCase()];
    if (!english || english.toLowerCase() === normalized.toLowerCase()) {
      return normalized;
    }
    return `${normalized} (${english})`;
  }, [language]);

  const selectedPlanIndex = useMemo(() => {
    if (!planExercises.length) return -1;
    return planLibrary.findIndex((template) => {
      const sameTitle = String(template.title || '').trim().toLowerCase() === String(sessionTitle || '').trim().toLowerCase();
      if (!sameTitle) return false;
      const templateNames = flattenPlanExercises(template.exercises).map((ex) => ex.name?.toLowerCase()).filter(Boolean);
      const planNames = planExerciseSlots.map((ex) => ex.name?.toLowerCase()).filter(Boolean);
      if (templateNames.length !== planNames.length) return false;
      return templateNames.every((name, idx) => name === planNames[idx]);
    });
  }, [planExerciseSlots, planExercises.length, planLibrary, sessionTitle]);

  const resolvedTheme = useMemo(() => {
    if (themeMode === 'auto') {
      return systemPrefersDark ? 'dark' : 'light';
    }
    return themeMode;
  }, [systemPrefersDark, themeMode]);
  const isLightTheme = resolvedTheme === 'light';
  const canUseOnlineFeatures = isOnline && !offlineMode;
  const canJoinSession = canUseOnlineFeatures;

  useEffect(() => {
    if (!librariesReady) return;
    const val = JSON.stringify(planLibrary);
    localStorage.setItem(PLAN_LIBRARY_KEY, val);
    if (IS_NATIVE) prefSet(PLAN_LIBRARY_KEY, val);
  }, [planLibrary, librariesReady]);

  useEffect(() => {
    if (!librariesReady) return;
    const val = JSON.stringify(templateLibrary);
    localStorage.setItem(TEMPLATE_LIBRARY_KEY, val);
    if (IS_NATIVE) prefSet(TEMPLATE_LIBRARY_KEY, val);
  }, [templateLibrary, librariesReady]);

  useEffect(() => {
    if (!librariesReady) return;
    const val = JSON.stringify(customExercises);
    localStorage.setItem(CUSTOM_EXERCISES_KEY, val);
    if (IS_NATIVE) prefSet(CUSTOM_EXERCISES_KEY, val);
  }, [customExercises, librariesReady]);

  useEffect(() => {
    if (!librariesReady) return;
    const val = JSON.stringify(trainingLog);
    localStorage.setItem(TRAINING_LOG_KEY, val);
    if (IS_NATIVE) prefSet(TRAINING_LOG_KEY, val);
  }, [trainingLog, librariesReady]);

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  useEffect(() => {
    const phase = snapshot?.state?.phase;
    const shouldPersist = offlineMode
      && !!snapshot
      && !!sessionId
      && phase
      && phase !== 'idle'
      && phase !== 'done';

    if (!shouldPersist) {
      localStorage.removeItem(ACTIVE_OFFLINE_SESSION_KEY);
      return;
    }

    try {
      const payload = JSON.stringify({
        sessionId,
        snapshot,
        planExercises,
        planFocus,
        sessionTitle,
        trainingPlanSelected,
        selectedDynamicSlots,
        isController,
        activePlanLibraryIndex,
        savedAt: Date.now()
      });
      localStorage.setItem(ACTIVE_OFFLINE_SESSION_KEY, payload);
    } catch (_error) {
    }
  }, [offlineMode, snapshot, sessionId, planExercises, planFocus, sessionTitle, trainingPlanSelected, selectedDynamicSlots, isController, activePlanLibraryIndex]);

  useEffect(() => {
    localStorage.setItem(THEME_MODE_KEY, themeMode);
    if (IS_NATIVE) prefSet(THEME_MODE_KEY, themeMode);
  }, [themeMode]);

  useEffect(() => {
    localStorage.setItem(LANGUAGE_KEY, language);
    if (IS_NATIVE) prefSet(LANGUAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    setSessionTitle((currentTitle) => (
      isDefaultSessionTitle(currentTitle) ? getDefaultSessionTitle(language) : currentTitle
    ));
  }, [language]);

  // On native: load from Preferences on first mount (overrides localStorage cache)
  useEffect(() => {
    if (!IS_NATIVE) return;
    Promise.all([
      prefGet(PLAN_LIBRARY_KEY),
      prefGet(CUSTOM_EXERCISES_KEY),
      prefGet(THEME_MODE_KEY),
      prefGet(LANGUAGE_KEY),
      prefGet(LAST_INDIVIDUAL_KEY),
      prefGet(TEMPLATE_LIBRARY_KEY),
      prefGet(TRAINING_LOG_KEY),
    ]).then(([plans, custom, theme, lang, lastInd, templates, logs]) => {
      if (plans) {
        try {
          const parsed = JSON.parse(plans);
          if (Array.isArray(parsed)) setPlanLibrary(parsed);
        } catch (_e) {}
      }
      if (templates) {
        try {
          const parsed = JSON.parse(templates);
          if (Array.isArray(parsed)) setTemplateLibrary(parsed);
        } catch (_e) {}
      }
      if (logs) {
        try { const parsed = JSON.parse(logs); if (Array.isArray(parsed)) setTrainingLog(parsed); } catch (_e) {}
      }
      if (custom) {
        try {
          const parsed = JSON.parse(custom);
          if (Array.isArray(parsed)) {
            setCustomExercises(normalizeCustomExercises(parsed));
          }
        } catch (_e) {}
      }
      if (theme && ['auto', 'dark', 'light'].includes(theme)) setThemeMode(theme);
      if (lang && SUPPORTED_LANGUAGES.includes(lang)) setLanguage(lang);
      if (lastInd) {
        try {
          const parsed = JSON.parse(lastInd);
          setLastIndividual({ ...PRESETS.classic63, ...parsed, name: 'individual' });
        } catch (_e) {}
      }
      setLibrariesReady(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    if (view !== 'join') return;
    if (!canJoinSession) {
      setJoinError(t('offlineJoinDisabled'));
      return;
    }
    if (joinError === t('offlineJoinDisabled')) {
      setJoinError('');
    }
  }, [canJoinSession, joinError, t, view]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => setSystemPrefersDark(event.matches);

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  const getAudioContext = useCallback(() => {
    // iOS can permanently close an AudioContext after an interruption. Do not
    // keep returning that unusable instance after a Watch/phone notification.
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      return audioContextRef.current;
    }
    audioContextRef.current = null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    const ctx = new AudioCtx();
    audioContextRef.current = ctx;
    return ctx;
  }, []);

  const resumeAudio = useCallback(async () => {
    const audioContext = getAudioContext();
    if (!audioContext) return false;
    if (audioContext.state !== 'running') {
      try {
        await audioContext.resume();
      } catch (_error) {
        setAudioReady(false);
        return false;
      }
    }
    const ready = audioContext.state === 'running';
    setAudioReady(ready);
    return ready;
  }, [getAudioContext]);

  const unlockAudio = useCallback(async () => {
    const audioContext = getAudioContext();
    if (!audioContext) return;
    await resumeAudio();
    // Play a silent buffer to fully unlock audio on iOS Safari
    try {
      const buf = audioContext.createBuffer(1, 1, audioContext.sampleRate);
      const src = audioContext.createBufferSource();
      src.buffer = buf;
      src.connect(audioContext.destination);
      src.start(0);
    } catch (_error) {
    }
    setAudioReady(audioContext.state === 'running');
  }, [getAudioContext, resumeAudio]);

  const activateWakeFallback = async () => {
    if (wakeFallbackPendingRef.current || !noSleepRef.current) return;
    wakeFallbackPendingRef.current = true;

    // iOS WebKit requires this video fallback to start inside a real user
    // gesture. This is especially important after a browser refresh, where
    // there is no gesture for an already-running session.
    unlockAudio();
    try {
      await noSleepRef.current.enable();
      if (!noSleepRef.current.isEnabled) throw new Error('Wake fallback did not start');
      noSleepEnabledRef.current = true;
      setWakeFallbackActive(true);
    } catch (_error) {
      // Keep the prompt visible so the user can retry with another tap.
      noSleepEnabledRef.current = false;
      setWakeFallbackActive(false);
    } finally {
      wakeFallbackPendingRef.current = false;
    }
  };

  const playBeep = useCallback(async (frequency, durationMs, gainValue = 0.5) => {
    const audioContext = getAudioContext();
    if (!audioContext) return;
    if (!(await resumeAudio())) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const now = audioContext.currentTime;
    const durationSeconds = durationMs / 1000;
    const attackSeconds = 0.005;
    const releaseSeconds = Math.min(0.025, durationSeconds * 0.3);
    const sustainEnd = now + Math.max(attackSeconds, durationSeconds - releaseSeconds);

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(frequency, now);

    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(gainValue, now + attackSeconds);
    gainNode.gain.setValueAtTime(gainValue, sustainEnd);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durationSeconds);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(now);
    oscillator.stop(now + durationSeconds + 0.02);
  }, [getAudioContext, resumeAudio]);

  const playDoubleBeep = useCallback(async (
    firstFrequency,
    secondFrequency,
    toneDurationMs = 90,
    gapMs = 70,
    gainValue = 0.55
  ) => {
    await playBeep(firstFrequency, toneDurationMs, gainValue);
    window.setTimeout(() => {
      playBeep(secondFrequency, toneDurationMs, gainValue);
    }, Math.max(30, toneDurationMs + gapMs));
  }, [playBeep]);

  const toggleSound = () => {
    // Create AudioContext synchronously inside the tap gesture (required by iOS Safari)
    const ctx = getAudioContext();
    if (ctx && ctx.state !== 'running') {
      resumeAudio().then((ready) => {
        if (!ready) return;
        try {
          const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
          const src = ctx.createBufferSource();
          src.buffer = buf;
          src.connect(ctx.destination);
          src.start(0);
        } catch (_e) {}
        setAudioReady(ctx.state === 'running');
      });
    }
    if (!audioReady && ctx && ctx.state === 'running') {
      setAudioReady(true);
    }
    setSoundEnabled((prev) => !prev);
  };

  const applyUiPrefsFromSnapshot = useCallback((nextSnapshot) => {
    const nextLanguage = nextSnapshot?.uiPrefs?.language;
    const nextThemeMode = nextSnapshot?.uiPrefs?.themeMode;

    if (SUPPORTED_LANGUAGES.includes(nextLanguage) && nextLanguage !== language) {
      setLanguage(nextLanguage);
    }
    if (['auto', 'dark', 'light'].includes(nextThemeMode) && nextThemeMode !== themeMode) {
      setThemeMode(nextThemeMode);
    }
  }, [language, themeMode]);

  const selectLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    savePlan();
  };

  const selectThemeMode = (nextMode) => {
    if (!['auto', 'dark', 'light'].includes(nextMode)) return;
    setThemeMode(nextMode);
    savePlan();
  };

  const setViewWithUrl = useCallback((nextView, sessionValue = sessionId, controllerValue = isController, replace = false) => {
    const url = new URL(window.location.href);
    const nextPath = nextView === 'timer' ? '/timer' : nextView === 'config' || nextView === 'configEditor' ? '/config' : '/session';
    url.pathname = nextPath;
    if (sessionValue) {
      url.searchParams.set('sessionId', sessionValue);
    } else {
      url.searchParams.delete('sessionId');
    }
    if (nextView === 'configEditor') {
      url.searchParams.set('screen', 'editor');
    } else {
      url.searchParams.delete('screen');
    }
    url.searchParams.set('role', controllerValue ? 'controller' : 'display');
    if (replace) {
      window.history.replaceState({}, '', url.toString());
    } else {
      window.history.pushState({}, '', url.toString());
    }
    setView(nextView);
  }, [isController, sessionId]);

  const parseSessionId = (value) => String(value || '').replace(/\D/g, '').slice(0, 4);

  const initOfflineSession = (targetView) => {
    const localId = Math.floor(1000 + Math.random() * 9000).toString();
    const localSnapshot = createIdleSnapshot(localId, configForm);
    const nextIsController = targetView !== 'session';
    setOfflineMode(true);
    setIsController(nextIsController);
    setJoinError('');
    setLinkMessage('');
    setSessionId(localId);
    snapshotRef.current = localSnapshot;
    setSnapshot(localSnapshot);
    setSelectedDynamicSlots({});
    setViewWithUrl(targetView, localId, nextIsController);
    return localId;
  };

  const createSession = async (targetView = 'session', includeCurrentPlan = true) => {
    const nextIsController = targetView !== 'session';
    if (!isOnline) {
      return initOfflineSession(targetView);
    }
    try {
      const res = await fetchWithTimeout(`${apiBase}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config: normalizeSessionConfig(configForm),
          title: includeCurrentPlan ? sessionTitle : getDefaultSessionTitle(language),
          plan: includeCurrentPlan && planExercises.length ? { focus: planFocus, exercises: planExercises } : null,
          uiPrefs: {
            language,
            themeMode
          }
        })
      });
      if (!res.ok) throw new Error('create failed');
      const data = await res.json();
      setOfflineMode(false);
      setIsController(nextIsController);
      setJoinError('');
      setLinkMessage('');
      setSessionId(data.sessionId);
      const nextSnapshot = normalizeSnapshot(data.snapshot);
      snapshotRef.current = nextSnapshot;
      setSnapshot(nextSnapshot);
      applyUiPrefsFromSnapshot(nextSnapshot);
      setSessionTitle(nextSnapshot?.title || getDefaultSessionTitle(language));
      setPlanFocus(nextSnapshot?.plan?.focus || 'full');
      setPlanExercises(normalizePlanExercises(nextSnapshot?.plan?.exercises));
      setTrainingPlanSelected(includeCurrentPlan ? hasSelectedTrainingPlan : inferTrainingPlanSelected(nextSnapshot?.title, nextSnapshot?.plan?.exercises));
      setSelectedDynamicSlots({});
      if (nextSnapshot?.scheduledStartAt) {
        const localValue = new Date(nextSnapshot.scheduledStartAt).toISOString().slice(0, 16);
        setScheduledStartInput(localValue);
      }
      setViewWithUrl(targetView, data.sessionId, nextIsController);
      return data.sessionId;
    } catch (_error) {
      return initOfflineSession(targetView);
    }
  };

  const joinSession = async (id, targetView = 'timer', controller = false) => {
    if (!canJoinSession) {
      setJoinError(t('offlineJoinDisabled'));
      return false;
    }
    const cleanId = parseSessionId(id);
    if (!/^\d{4}$/.test(cleanId)) {
      setJoinError(t('invalidSession'));
      return false;
    }

    try {
      const res = await fetchWithTimeout(`${apiBase}/session/${cleanId}`);
      if (!res.ok) throw new Error('invalid');
      const snap = normalizeSnapshot(await res.json());
      setOfflineMode(false);
      setIsController(controller);
      setJoinError('');
      setLinkMessage('');
      setSessionId(cleanId);
      snapshotRef.current = snap;
      setSnapshot(snap);
      applyUiPrefsFromSnapshot(snap);
      setSessionTitle(snap?.title || getDefaultSessionTitle(language));
      setPlanFocus(snap?.plan?.focus || 'full');
      setPlanExercises(normalizePlanExercises(snap?.plan?.exercises));
      setTrainingPlanSelected(inferTrainingPlanSelected(snap?.title, snap?.plan?.exercises));
      setSelectedDynamicSlots({});
      if (snap?.scheduledStartAt) {
        const localValue = new Date(snap.scheduledStartAt).toISOString().slice(0, 16);
        setScheduledStartInput(localValue);
      }
      setViewWithUrl(targetView, cleanId, controller);
      return true;
    } catch (_error) {
      setJoinError(isOnline ? t('invalidSession') : t('offlineJoinDisabled'));
      return false;
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const role = url.searchParams.get('role');
    const controllerFromRole = role === 'controller';
    const urlInitialView = url.pathname.startsWith('/timer') ? 'timer' : 'session';
    setView(urlInitialView);
    setIsController(controllerFromRole);
    const existing = url.searchParams.get('sessionId');

    const tryRestoreOfflineSession = () => {
      let raw;
      try {
        raw = localStorage.getItem(ACTIVE_OFFLINE_SESSION_KEY);
      } catch (_error) {
        return false;
      }
      if (!raw) return false;
      try {
        const data = JSON.parse(raw);
        const phase = data?.snapshot?.state?.phase;
        const savedAt = Number(data?.savedAt) || 0;
        const isFresh = Date.now() - savedAt < ACTIVE_OFFLINE_SESSION_MAX_AGE_MS;
        const isActive = phase && phase !== 'idle' && phase !== 'done';
        if (!data?.sessionId || !data?.snapshot || !isActive || !isFresh) {
          localStorage.removeItem(ACTIVE_OFFLINE_SESSION_KEY);
          return false;
        }
        setOfflineMode(true);
        setSessionId(data.sessionId);
        const restoredSnapshot = normalizeSnapshot(data.snapshot);
        snapshotRef.current = restoredSnapshot;
        setSnapshot(restoredSnapshot);
        setPlanExercises(normalizePlanExercises(data.planExercises));
        if (data.planFocus) setPlanFocus(data.planFocus);
        if (data.sessionTitle) setSessionTitle(data.sessionTitle);
        setTrainingPlanSelected(Boolean(data.trainingPlanSelected));
        setSelectedDynamicSlots(data.selectedDynamicSlots || {});
        if (typeof data.isController === 'boolean') setIsController(data.isController);
        if (typeof data.activePlanLibraryIndex === 'number') {
          setActivePlanLibraryIndex(data.activePlanLibraryIndex);
        }
        setViewWithUrl('timer', data.sessionId, data.isController !== false, true);
        return true;
      } catch (_error) {
        localStorage.removeItem(ACTIVE_OFFLINE_SESSION_KEY);
        return false;
      }
    };

    const init = async () => {
      if (existing) {
        const didJoin = await joinSession(existing, urlInitialView, controllerFromRole);
        if (didJoin) {
          return;
        }
        if (tryRestoreOfflineSession()) {
          return;
        }
        setSessionExpiredVisible(true);
        setJoinError('');
        createSession('session', false);
        return;
      }
      if (tryRestoreOfflineSession()) {
        return;
      }
      createSession(urlInitialView === 'join' ? 'session' : urlInitialView, false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const url = new URL(window.location.href);
      const screen = url.searchParams.get('screen');
      const nextView = url.pathname.startsWith('/timer')
        ? 'timer'
        : url.pathname.startsWith('/config')
          ? (screen === 'editor' ? 'configEditor' : 'config')
          : 'session';
      setView(nextView);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const refreshSnapshot = useCallback(async (targetSessionId = sessionId) => {
      if (!targetSessionId || offlineMode) return;
    try {
      const res = await fetchWithTimeout(`${apiBase}/session/${targetSessionId}`);
      if (!res.ok) return;
      const snap = normalizeSnapshot(await res.json());
      snapshotRef.current = snap;
      setSnapshot(snap);
      applyUiPrefsFromSnapshot(snap);
      if (snap?.sessionId && snap.sessionId !== targetSessionId) {
        setSessionId(snap.sessionId);
      }
    } catch (_error) {
    }
  }, [apiBase, applyUiPrefsFromSnapshot, offlineMode, sessionId]);

  const commitLocalSnapshot = useCallback((nextSnapshot, markDirty = true) => {
    const normalized = normalizeSnapshot(nextSnapshot);
    snapshotRef.current = normalized;
    setSnapshot(normalized);
    if (markDirty) {
      syncDirtyRef.current = true;
    }
    return normalized;
  }, []);

  const syncSnapshotToServer = useCallback(async (targetSessionId = sessionId, sourceSnapshot = snapshotRef.current) => {
    if (!targetSessionId || offlineMode || !isOnline || !sourceSnapshot) return null;

    const normalized = normalizeSnapshot(sourceSnapshot);
    const expectedUpdatedAt = normalized?.state?.updatedAt || 0;
    const nextTitle = typeof normalized?.title === 'string' ? normalized.title : sessionTitle;
    const nextPlan = Object.prototype.hasOwnProperty.call(normalized || {}, 'plan')
      ? normalized.plan
      : (planExercises.length ? { focus: planFocus, exercises: planExercises } : null);

    try {
      const res = await fetchWithTimeout(`${apiBase}/session/${targetSessionId}/state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config: normalized.config,
          title: nextTitle,
          scheduledStartAt: normalized.scheduledStartAt || null,
          plan: nextPlan,
          state: normalized.state
        })
      });
      if (!res.ok) return null;
      if ((snapshotRef.current?.state?.updatedAt || 0) === expectedUpdatedAt) {
        syncDirtyRef.current = false;
      }
      return normalizeSnapshot(await res.json());
    } catch (_error) {
      return null;
    }
  }, [apiBase, isOnline, offlineMode, planExercises, planFocus, sessionId, sessionTitle]);

  useEffect(() => {
    if (!serverPersistenceEnabled || !sessionId || !syncDirtyRef.current) return;
    syncSnapshotToServer(sessionId, snapshotRef.current);
  }, [serverPersistenceEnabled, sessionId, syncSnapshotToServer]);

  useEffect(() => {
    if (!sessionId || offlineMode) return undefined;

    let disposed = false;

    const clearReconnectTimer = () => {
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed || offlineMode || wsManualCloseRef.current) return;
      if (wsReconnectTimerRef.current) return;
      const attempt = wsReconnectAttemptRef.current;
      const delay = Math.min(WS_RECONNECT_MAX_MS, WS_RECONNECT_BASE_MS * (2 ** attempt));
      wsReconnectTimerRef.current = setTimeout(() => {
        wsReconnectTimerRef.current = null;
        if (!disposed && !offlineMode) {
          connect();
        }
      }, delay);
      wsReconnectAttemptRef.current = Math.min(attempt + 1, 8);
    };

    const connect = () => {
      if (disposed || offlineMode || !sessionId) return;

      if (wsRef.current) {
        wsManualCloseRef.current = true;
        wsRef.current.close();
        wsRef.current = null;
        wsManualCloseRef.current = false;
      }

      const ws = new WebSocket(`${apiBase.replace(/^http/, 'ws')}/ws?sessionId=${sessionId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        wsReconnectAttemptRef.current = 0;
        clearReconnectTimer();
      };

      ws.onmessage = (evt) => {
        try {
          const message = JSON.parse(evt.data);
          if (message.type === 'state:update') {
            const incomingSnapshot = normalizeSnapshot(message.payload);
            const localSnapshot = snapshotRef.current;
            const nextSnapshot = syncDirtyRef.current
              && localSnapshot
              && (incomingSnapshot?.state?.updatedAt || 0) < (localSnapshot?.state?.updatedAt || 0)
              ? { ...localSnapshot, sync: incomingSnapshot?.sync || localSnapshot?.sync }
              : incomingSnapshot;
            snapshotRef.current = nextSnapshot;
            setSnapshot(nextSnapshot);
            applyUiPrefsFromSnapshot(nextSnapshot);
            if (nextSnapshot?.sessionId && nextSnapshot.sessionId !== sessionId) {
              setSessionId(nextSnapshot.sessionId);
              if (view === 'session') {
                setViewWithUrl('timer', nextSnapshot.sessionId, isController);
              }
            }
          }
        } catch (_error) {
        }
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch (_error) {
        }
      };

      ws.onclose = () => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
        if (!disposed && !wsManualCloseRef.current) {
          scheduleReconnect();
        }
      };
    };

    wsManualCloseRef.current = false;
    connect();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshSnapshot(sessionId);
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
          clearReconnectTimer();
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearReconnectTimer();
      wsReconnectAttemptRef.current = 0;
      wsManualCloseRef.current = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      wsManualCloseRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, apiBase, offlineMode, view, applyUiPrefsFromSnapshot, isController, refreshSnapshot]);

  useEffect(() => {
    if (!snapshot?.state?.running) return undefined;
    const timeout = setTimeout(() => {
      const currentSnapshot = snapshotRef.current;
      if (!currentSnapshot?.state?.running) return;
      const nextSnapshot = advanceOffline(currentSnapshot);
      commitLocalSnapshot(nextSnapshot);
      if (serverPersistenceEnabled && sessionId) {
        syncSnapshotToServer(sessionId, nextSnapshot);
      }
    }, Math.max(50, getRemainingMs(snapshot.state)));
    return () => clearTimeout(timeout);
  }, [commitLocalSnapshot, serverPersistenceEnabled, sessionId, snapshot, syncSnapshotToServer]);

  useEffect(() => {
    if (!snapshot?.state?.running) return undefined;
    const interval = setInterval(() => {
      setRenderTick(Date.now());
    }, 50);
    return () => clearInterval(interval);
  }, [snapshot?.state?.running, snapshot?.state?.phase]);

  useEffect(() => {
    if (!snapshot?.scheduledStartAt) return undefined;
    const interval = setInterval(() => {
      setRenderTick(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [snapshot?.scheduledStartAt]);

  useEffect(() => {
    if (!state || !soundEnabled || !audioReady) return;

    if (!state.running || state.phase === 'idle' || state.phase === 'done') {
      playedCueIdsRef.current.clear();
      return;
    }

    const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
    if (secondsLeft < 1) return;

    const isThreeTwoOnePhase = state.phase === 'countdown'
      || state.phase === 'rest'
      || state.phase === 'roundRest'
      || state.phase === 'supersetPause';

    if (isThreeTwoOnePhase && secondsLeft <= 3) {
      const cueId = `${sessionId}-${state.phase}-${state.currentRound}-${state.currentSet}-${state.segmentInSet || 1}-${secondsLeft}`;
      if (!playedCueIdsRef.current.has(cueId)) {
        playedCueIdsRef.current.add(cueId);
        playBeep(1240, 120, 0.55);
      }
      return;
    }

    if (state.phase === 'active' && secondsLeft === 1) {
      const cueId = `${sessionId}-active-last-${state.currentRound}-${state.currentSet}-${state.segmentInSet || 1}`;
      if (!playedCueIdsRef.current.has(cueId)) {
        playedCueIdsRef.current.add(cueId);
        playBeep(880, 520, 0.7);
      }
      return;
    }

    if (state.phase === 'active') {
      const midThresholdMs = Math.floor((state.blockDurationMs || 0) / 2);
      if (midThresholdMs > 0 && remainingMs <= midThresholdMs && remainingMs >= midThresholdMs - 350) {
        const cueId = `${sessionId}-active-mid-${state.currentRound}-${state.currentSet}-${state.segmentInSet || 1}`;
        if (!playedCueIdsRef.current.has(cueId)) {
          playedCueIdsRef.current.add(cueId);
          playDoubleBeep(980, 760, 85, 55, 0.58);
        }
      }
    }
  }, [audioReady, playBeep, playDoubleBeep, remainingMs, sessionId, soundEnabled, state]);

  useEffect(() => {
    if (state?.phase !== 'done') {
      setCompletionMessageIndex(0);
      return undefined;
    }

    const interval = setInterval(() => {
      setCompletionMessageIndex((prev) => (prev + 1) % 2);
    }, 1500);
    return () => clearInterval(interval);
  }, [state?.phase]);

  useEffect(() => {
    if (state?.phase === 'done') {
      if (!hasLoggedDoneRef.current) {
        hasLoggedDoneRef.current = true;
        const entry = {
          id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
          completedAt: new Date().toISOString(),
          planTitle: sessionTitle || '',
          planFocus: planFocus || '',
          exercises: (planExercises || []).map((e) => ({
            name: e.name, focus: getExerciseFocus(e, customExercises), equipment: e.equipment, weight: e.weight, mode: e.mode,
            supersetExercise: e.supersetExercise || null
          })),
          config: {
            rounds: config?.rounds, sets: config?.sets, activeMs: config?.activeMs,
            restMs: config?.restMs, roundRestMs: config?.roundRestMs
          }
        };
        setTrainingLog((prev) => {
          const recent = prev[0];
          const TEN_MIN_MS = 10 * 60 * 1000;
          if (recent) {
            const recentMs = Date.parse(recent.completedAt);
            const sameTitle = String(recent.planTitle || '') === String(entry.planTitle || '');
            const recentNames = (recent.exercises || []).map((e) => e.name).join('|');
            const entryNames = (entry.exercises || []).map((e) => e.name).join('|');
            const sameExercises = recentNames === entryNames;
            if (sameTitle && sameExercises && Number.isFinite(recentMs) && (Date.now() - recentMs) < TEN_MIN_MS) {
              const merged = {
                ...recent,
                completedAt: entry.completedAt,
                repeatCount: (recent.repeatCount || 1) + 1,
                config: entry.config,
                exercises: entry.exercises
              };
              return [merged, ...prev.slice(1)].slice(0, 200);
            }
          }
          return [entry, ...prev].slice(0, 200);
        });
      }
    } else {
      hasLoggedDoneRef.current = false;
    }
  }, [
    config?.activeMs,
    config?.restMs,
    config?.roundRestMs,
    config?.rounds,
    config?.sets,
    planExercises,
    customExercises,
    planFocus,
    sessionTitle,
    state?.phase
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // NoSleep uses a tiny, looping video where the Screen Wake Lock API is
    // unavailable (notably iOS WebKit).
    noSleepRef.current = new NoSleep();
    const video = noSleepRef.current.noSleepVideo;
    const handleWakeFallbackStopped = () => {
      noSleepEnabledRef.current = false;
      setWakeFallbackActive(false);
    };
    video?.addEventListener('pause', handleWakeFallbackStopped);
    video?.addEventListener('error', handleWakeFallbackStopped);
    return () => {
      video?.removeEventListener('pause', handleWakeFallbackStopped);
      video?.removeEventListener('error', handleWakeFallbackStopped);
      if (noSleepEnabledRef.current && noSleepRef.current) {
        try {
          noSleepRef.current.disable();
        } catch (_error) {
        }
      }
      noSleepEnabledRef.current = false;
      wakeFallbackPendingRef.current = false;
      noSleepRef.current = null;
    };
  }, []);

  useEffect(() => {
    const onTouch = () => {
      // Eagerly create + resume AudioContext on first touch (iOS Safari requirement)
      const ctx = getAudioContext();
      if (ctx && ctx.state !== 'running') {
        resumeAudio().then((ready) => {
          if (!ready) return;
          try {
            const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
            const src = ctx.createBufferSource();
            src.buffer = buf;
            src.connect(ctx.destination);
            src.start(0);
          } catch (_e) {}
          setAudioReady(ctx.state === 'running');
        });
      } else if (ctx && ctx.state === 'running') {
        setAudioReady(true);
      }

      // iOS Safari requires the video-based NoSleep fallback to be started
      // directly from a user gesture. If a timer is already running (or
      // scheduled), use this touch/click to acquire the fallback immediately.
      // This also restores it after Safari temporarily releases it.
      const shouldKeepAwake = Boolean(
        snapshotRef.current?.state?.running || snapshotRef.current?.scheduledStartAt
      );
      if (shouldKeepAwake && noSleepRef.current && !noSleepEnabledRef.current) {
        try {
          noSleepRef.current.enable();
          noSleepEnabledRef.current = true;
          setWakeFallbackActive(true);
        } catch (_error) {
          noSleepEnabledRef.current = false;
          setWakeFallbackActive(false);
        }
      }
    };
    window.addEventListener('touchstart', onTouch, { passive: true });
    window.addEventListener('click', onTouch, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('click', onTouch);
    };
  }, [getAudioContext, resumeAudio]);

  useEffect(() => {
    const restoreAfterInterruption = () => {
      if (document.visibilityState !== 'visible') {
        setAudioReady(false);
        setWakeFallbackActive(false);
        return;
      }

      // Safari moves Web Audio to "interrupted" when a system notification is
      // shown. A normal resume only checks for "suspended", which left timer
      // cues silent until the whole page was reloaded.
      if (soundEnabled) resumeAudio();

    };

    document.addEventListener('visibilitychange', restoreAfterInterruption);
    window.addEventListener('focus', restoreAfterInterruption);
    window.addEventListener('pageshow', restoreAfterInterruption);
    return () => {
      document.removeEventListener('visibilitychange', restoreAfterInterruption);
      window.removeEventListener('focus', restoreAfterInterruption);
      window.removeEventListener('pageshow', restoreAfterInterruption);
    };
  }, [resumeAudio, snapshot?.scheduledStartAt, soundEnabled, state?.running]);

  useEffect(() => {
    if (!noSleepRef.current) return undefined;

    if (shouldKeepScreenAwake && wakeFallbackActive && !noSleepEnabledRef.current) {
      try {
        noSleepRef.current.enable();
        noSleepEnabledRef.current = true;
      } catch (_error) {
      }
    }

    if ((!shouldKeepScreenAwake || !wakeFallbackActive) && noSleepEnabledRef.current) {
      try {
        noSleepRef.current.disable();
      } catch (_error) {
      }
      noSleepEnabledRef.current = false;
    }

    return undefined;
  }, [shouldKeepScreenAwake, wakeFallbackActive]);

  // Sync active session to Wear OS watch
  useEffect(() => {
    if (!IS_NATIVE || view !== 'timer' || !sessionId) return;
    WearSync.syncSession({ sessionId }).catch(() => {});
  }, [view, sessionId]);

  useEffect(() => {
    if (!('wakeLock' in navigator) || typeof navigator.wakeLock?.request !== 'function') {
      return undefined;
    }

    let cancelled = false;

    const requestWakeLock = async () => {
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          lock.release().catch(() => {});
          return;
        }
        wakeLockRef.current = lock;
        lock.addEventListener('release', () => {
          if (wakeLockRef.current === lock) {
            wakeLockRef.current = null;
          }
        });
      } catch (_error) {
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  const updateConfig = async (payload, targetSessionId = sessionId) => {
    if (!targetSessionId) return null;
    const normalizedPayload = normalizeSessionConfig(payload);
    const localSnapshot = normalizeSnapshot({
      ...(snapshotRef.current || {}),
      sessionId: targetSessionId,
      config: normalizedPayload,
      title: sessionTitle,
      plan: planExercises.length ? { focus: planFocus, exercises: planExercises } : null,
      state: createIdleSnapshot(targetSessionId, normalizedPayload).state
    });
    commitLocalSnapshot(localSnapshot);

    if (!isOnline) {
      setOfflineMode(true);
      return localSnapshot;
    }

    if (serverPersistenceEnabled) {
      syncSnapshotToServer(targetSessionId, localSnapshot);
    }

    return localSnapshot;
  };

  const savePlan = async (targetSessionId = sessionId) => {
    if (!targetSessionId) return;
    const nextSnapshot = normalizeSnapshot({
      ...(snapshotRef.current || {}),
      sessionId: targetSessionId,
      config: snapshotRef.current?.config || config,
      title: sessionTitle,
      plan: planExercises.length ? { focus: planFocus, exercises: planExercises } : null,
      state: snapshotRef.current?.state || createIdleSnapshot(targetSessionId, config).state
    });
    commitLocalSnapshot(nextSnapshot);
    syncDirtyRef.current = true;
    if (!serverPersistenceEnabled) return;
    await syncSnapshotToServer(targetSessionId, nextSnapshot);
  };

  const saveScheduledStart = async () => {
    if (!sessionId) return;
    const isoValue = scheduledStartInput ? new Date(scheduledStartInput).toISOString() : null;
    const nextSnapshot = normalizeSnapshot({
      ...(snapshotRef.current || {}),
      sessionId,
      config,
      scheduledStartAt: isoValue,
      state: snapshotRef.current?.state || createIdleSnapshot(sessionId, config).state
    });
    commitLocalSnapshot(nextSnapshot);
    if (!serverPersistenceEnabled) return;
    await syncSnapshotToServer(sessionId, nextSnapshot);
  };

  const ensureOnlineSessionForLink = async () => {
    if (!offlineMode && sessionId) {
      return sessionId;
    }

    const configPayload = normalizeSessionConfig(snapshot?.config || configForm);
    const res = await fetch(`${apiBase}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config: configPayload,
        title: sessionTitle,
        plan: planExercises.length ? { focus: planFocus, exercises: planExercises } : null,
        uiPrefs: {
          language,
          themeMode
        }
      })
    });

    if (!res.ok) {
      throw new Error('online session create failed');
    }

    const data = await res.json();
    const nextSnapshot = normalizeSnapshot(data.snapshot);
    setOfflineMode(false);
    setIsController(true);
    setSessionId(data.sessionId);
    snapshotRef.current = nextSnapshot;
    setSnapshot(nextSnapshot);
    applyUiPrefsFromSnapshot(nextSnapshot);
    setViewWithUrl(view, data.sessionId, true, true);
    await syncSnapshotToServer(data.sessionId, {
      ...normalizeSnapshot(snapshotRef.current || nextSnapshot),
      sessionId: data.sessionId
    });
    return data.sessionId;
  };

  const handleControl = async (action) => {
    await unlockAudio();

    let activeSessionId = sessionId;
    if (!activeSessionId) {
      activeSessionId = await createSession('timer');
      if (!activeSessionId) return;
    }

    const liveSnapshot = snapshotRef.current || snapshot;
    if (!liveSnapshot) return;

    if (action === 'play' && needsDynamicSelectionBeforeStart) {
      openDynamicExercisePicker();
      return;
    }

    const nextSnapshot = stepOfflineControlSnapshot(liveSnapshot, action);
    commitLocalSnapshot(nextSnapshot);

    if (action === 'reset') {
      setSelectedDynamicSlots({});
    }

    if (serverPersistenceEnabled) {
      syncSnapshotToServer(activeSessionId, nextSnapshot);
    }
  };

  const handlePrevControl = () => {
    const now = Date.now();
    if (now - prevTapAtRef.current <= 380) {
      prevTapAtRef.current = 0;
      handleControl('jumpLast');
      return;
    }

    prevTapAtRef.current = now;
    handleControl('prev');
  };

  const leaveSessionToHome = useCallback(async () => {
    const activeSessionId = sessionId;

    if (activeSessionId && serverPersistenceEnabled) {
      try {
        await fetch(`${apiBase}/session/${activeSessionId}/control`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'reset' })
        });
      } catch (_error) {
      }
    }

    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = null;
    }
    wsReconnectAttemptRef.current = 0;
    wsManualCloseRef.current = true;
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (_error) {
      }
      wsRef.current = null;
    }
    wsManualCloseRef.current = false;

    setSessionId('');
    setSnapshot(null);
    setJoinDigits(['', '', '', '']);
    setJoinError('');
    setLinkMessage('');
    setIsController(false);
    setScheduledStartInput('');
    setActivePlanLibraryIndex(null);
    setSelectedDynamicSlots({});
    snapshotRef.current = null;
    syncDirtyRef.current = false;

    setViewWithUrl('session', '', false);
  }, [apiBase, serverPersistenceEnabled, sessionId, setViewWithUrl]);

  const linkDeviceToCurrentSession = async (deviceCodeRaw) => {
    if (!sessionId) return;

    setLinkMessage('');
    const deviceCode = parseSessionId(deviceCodeRaw);
    if (!/^\d{4}$/.test(deviceCode)) {
      setLinkMessage(t('invalidSession'));
      return;
    }

    if (deviceCode === sessionId) {
      setLinkMessage(t('deviceOwnIdError'));
      return;
    }

    try {
      let targetSessionId = sessionId;
      if (offlineMode) {
        setLinkMessage(t('switchingOnline'));
        targetSessionId = await ensureOnlineSessionForLink();
      }

      if (deviceCode === targetSessionId) {
        setLinkMessage(t('deviceOwnIdError'));
        return;
      }

      await syncSnapshotToServer(targetSessionId, snapshotRef.current);

      const res = await fetch(`${apiBase}/session/${targetSessionId}/link-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceCode })
      });

      if (!res.ok) {
        setLinkMessage(t('invalidSession'));
        return;
      }

      const data = await res.json();
      if (data?.snapshot) {
        const linkedSnapshot = normalizeSnapshot(data.snapshot);
        snapshotRef.current = linkedSnapshot;
        setSnapshot(linkedSnapshot);
        applyUiPrefsFromSnapshot(linkedSnapshot);
      }
      setLinkMessage(t('deviceAdded', { code: deviceCode }));
      setDeviceCodeInput('');
    } catch (_error) {
      setLinkMessage(t('onlineUnavailable'));
    }
  };

  const openConfigEditor = (mode) => {
    setIsController(true);
    setConfigEditorMode(mode);
    if (mode === 'individuell') {
      setConfigEditorForm({ ...lastIndividual, name: 'individual', dynamicTraining: false });
    } else {
      setConfigEditorForm({ ...PRESETS[mode], name: mode, dynamicTraining: false });
    }
    setConfigEditorOpen(true);
  };

  const loadTemplate = (template) => {
    const config = template.config;
    setIsController(true);
    setConfigEditorMode(template.builtIn ? template.id : 'individuell');
    setConfigEditorForm({
      rounds: config.rounds,
      sets: config.sets,
      activeMs: config.activeMs,
      restMs: config.restMs,
      roundRestMs: config.roundRestMs,
      supersetEnabled: Boolean(config.supersetEnabled),
      dynamicTraining: Boolean(config.dynamicTraining),
      name: template.builtIn ? template.id : 'individual'
    });
    setTemplateOverlayOpen(false);
    setConfigEditorOpen(true);
  };

  const openSaveTemplatePrompt = () => {
    setTemplateNameInput('');
    setTemplateNameError('');
    setTemplateSavePromptOpen(true);
  };

  const saveCurrentAsTemplate = () => {
    const name = String(templateNameInput || '').trim();
    if (!name) {
      setTemplateNameError(t('templateNamePlaceholder'));
      return;
    }
    const normalizedName = name.toLowerCase();
    const duplicate = templateLibrary.some((tpl) => String(tpl.title || '').trim().toLowerCase() === normalizedName);
    if (duplicate) {
      setTemplateNameError(t('templateNameExists'));
      return;
    }
    const newTemplate = {
      id: `tpl-${Date.now()}`,
      title: name.slice(0, 60),
      builtIn: false,
      config: {
        rounds: Math.max(1, Number(configEditorForm.rounds) || 1),
        sets: Math.max(1, Number(configEditorForm.sets) || 1),
        activeMs: Math.max(1000, Number(configEditorForm.activeMs) || 1000),
        restMs: Math.max(1000, Number(configEditorForm.restMs) || 1000),
        roundRestMs: Math.max(1000, Number(configEditorForm.roundRestMs) || 1000),
        supersetEnabled: Boolean(configEditorForm.supersetEnabled),
        dynamicTraining: Boolean(configEditorForm.dynamicTraining)
      }
    };
    setTemplateLibrary((prev) => [...prev, newTemplate]);
    setTemplateSavePromptOpen(false);
  };

  const deleteTemplate = (templateId) => {
    setTemplateLibrary((prev) => prev.filter((tpl) => tpl.id !== templateId));
  };

  const saveConfigAndShowTimer = async () => {
    const nextConfig = {
      ...configEditorForm,
      rounds: Math.max(1, Number(configEditorForm.rounds) || 1),
      sets: Math.max(1, Number(configEditorForm.sets) || 1),
      activeMs: Math.max(1_000, Number(configEditorForm.activeMs) || 1_000),
      restMs: Math.max(1_000, Number(configEditorForm.restMs) || 1_000),
      roundRestMs: Math.max(1_000, Number(configEditorForm.roundRestMs) || 1_000),
      dynamicTraining: Boolean(configEditorForm.dynamicTraining)
    };

    if (configEditorMode === 'individuell') {
      const individualConfig = { ...nextConfig, name: 'individual' };
      setLastIndividual(individualConfig);
      const indVal = JSON.stringify(individualConfig);
      localStorage.setItem(LAST_INDIVIDUAL_KEY, indVal);
      if (IS_NATIVE) prefSet(LAST_INDIVIDUAL_KEY, indVal);
    }

    if (!nextConfig.dynamicTraining && planExercises.length > 0 && planExercises.length < requiredExerciseCount) {
      setPlanCountWarningOpen(true);
      return;
    }

    let activeSessionId = sessionId;
    if (!activeSessionId) {
      activeSessionId = await createSession('session');
      if (!activeSessionId) return;
    }

    setConfigForm(nextConfig);
    await updateConfig(nextConfig, activeSessionId);
    await savePlan(activeSessionId);
    setIsController(true);
    setViewWithUrl('timer', activeSessionId, true);
  };

  const proceedWithFewerExercises = async () => {
    setPlanCountWarningOpen(false);

    const nextConfig = {
      ...configEditorForm,
      rounds: Math.max(1, Number(configEditorForm.rounds) || 1),
      sets: Math.max(1, Number(configEditorForm.sets) || 1),
      activeMs: Math.max(1_000, Number(configEditorForm.activeMs) || 1_000),
      restMs: Math.max(1_000, Number(configEditorForm.restMs) || 1_000),
      roundRestMs: Math.max(1_000, Number(configEditorForm.roundRestMs) || 1_000),
      dynamicTraining: Boolean(configEditorForm.dynamicTraining)
    };

    if (configEditorMode === 'individuell') {
      const individualConfig = { ...nextConfig, name: 'individual' };
      setLastIndividual(individualConfig);
      const indVal = JSON.stringify(individualConfig);
      localStorage.setItem(LAST_INDIVIDUAL_KEY, indVal);
      if (IS_NATIVE) prefSet(LAST_INDIVIDUAL_KEY, indVal);
    }

    let activeSessionId = sessionId;
    if (!activeSessionId) {
      activeSessionId = await createSession('session');
      if (!activeSessionId) return;
    }

    setConfigForm(nextConfig);
    await updateConfig(nextConfig, activeSessionId);
    await savePlan(activeSessionId);
    setIsController(true);
    setViewWithUrl('timer', activeSessionId, true);
  };

  const openExercisePicker = () => {
    setExercisePickerMode('plan');
    setExerciseFocusFilters([]);
    setExercisePickerSelection(planExerciseSlots.map((entry) => entry.name));
    setExerciseFilterInput('');
    setCustomExerciseInput('');
    setCustomExerciseError('');
    setCustomExerciseModalOpen(false);
    setExercisePickerOpen(true);
  };

  const toggleExerciseSelection = (name) => {
    setExercisePickerSelection((prev) => {
      if (exercisePickerMode === 'dynamic') {
        const key = name.toLowerCase();
        const exists = prev.some((entry) => entry.toLowerCase() === key);
        if (exists) {
          return prev.filter((entry) => entry.toLowerCase() !== key);
        }
        if (prev.length >= 2) {
          return [prev[0], name];
        }
        return [...prev, name];
      }
      const exists = prev.some((entry) => entry.toLowerCase() === name.toLowerCase());
      if (exists) {
        return prev.filter((entry) => entry.toLowerCase() !== name.toLowerCase());
      }
      return [...prev, name];
    });
  };

  const openDynamicExercisePicker = useCallback(() => {
    const currentSlot = currentPlanExerciseSlotIndex >= 0 ? planExercises[currentPlanExerciseSlotIndex] : null;
    const preselectedExercises = [];
    if (currentSlot?.name) {
      preselectedExercises.push(currentSlot.name);
    }
    if (normalizeExerciseMode(currentSlot?.mode) === 'SZ' && currentSlot?.supersetExercise?.name) {
      preselectedExercises.push(currentSlot.supersetExercise.name);
    }
    setExercisePickerMode('dynamic');
    setExerciseFocusFilters([]);
    setExercisePickerSelection(preselectedExercises.slice(0, 2));
    setExerciseFilterInput('');
    setCustomExerciseInput('');
    setCustomExerciseError('');
    setCustomExerciseModalOpen(false);
    setExercisePickerOpen(true);
  }, [currentPlanExerciseSlotIndex, planExercises]);

  useEffect(() => {
    if (!canPickDynamicExercise) return;
    const slotKey = `slot-${currentProgressSlotIndex}`;
    if (selectedDynamicSlots[slotKey]) return;
    if (exercisePickerOpen) return;
    if (autoOpenedDynamicPromptRef.current === dynamicSelectionPromptKey) return;
    autoOpenedDynamicPromptRef.current = dynamicSelectionPromptKey;
    openDynamicExercisePicker();
  }, [
    canPickDynamicExercise,
    currentProgressSlotIndex,
    dynamicSelectionPromptKey,
    exercisePickerOpen,
    openDynamicExercisePicker,
    selectedDynamicSlots
  ]);

  const applyExerciseSelection = () => {
    if (exercisePickerMode === 'dynamic') {
      const selectedNames = exercisePickerSelection
        .map((name) => normalizeExerciseName(name))
        .filter(Boolean)
        .slice(0, 2);
      if (selectedNames.length === 0) return;

      let snapshotExercises = null;
      const slotKey = `slot-${currentProgressSlotIndex}`;
      setPlanExercises((prev) => {
        const targetIndex = Math.min(currentProgressSlotIndex, prev.length);
        const next = [...prev];
        const currentEntry = next[targetIndex] ? normalizePlanExercise(next[targetIndex]) : null;
        const matchingPrimaryEntry = findMatchingExerciseEntry(prev, selectedNames[0]);
        const primaryEntry = { ...buildDynamicExerciseEntry(selectedNames[0], currentEntry, matchingPrimaryEntry), focus: exerciseFocus(selectedNames[0]) };

        if (selectedNames.length === 2) {
          const currentSupersetEntry = currentEntry?.supersetExercise?.name
            ? normalizePlanExercise({ ...currentEntry.supersetExercise, mode: 'NR' })
            : null;
          const matchingSupersetEntry = findMatchingExerciseEntry(prev, selectedNames[1]);
          const supersetEntry = { ...buildDynamicExerciseEntry(selectedNames[1], currentSupersetEntry, matchingSupersetEntry), focus: exerciseFocus(selectedNames[1]) };
          next[targetIndex] = normalizePlanExercise({
            ...primaryEntry,
            mode: 'SZ',
            supersetExercise: supersetEntry
          });
        } else {
          next[targetIndex] = normalizePlanExercise({
            ...primaryEntry,
            mode: 'NR'
          });
        }
        snapshotExercises = normalizePlanExercises(next);
        return snapshotExercises;
      });
      setSelectedDynamicSlots((prev) => ({ ...prev, [slotKey]: true }));

      if (snapshotExercises && sessionId && !offlineMode) {
        window.setTimeout(() => savePlan(sessionId), 0);
      }

      setExercisePickerSelection([]);
      setExerciseFilterInput('');
      setCustomExerciseInput('');
      setCustomExerciseError('');
      setCustomExerciseModalOpen(false);
      setExercisePickerOpen(false);
      setExercisePickerMode('plan');
      return;
    }

    const selectedSet = new Set(exercisePickerSelection.map((entry) => entry.toLowerCase()));

    setPlanExercises((prev) => {
      const existingByName = new Map();
      flattenPlanExercises(prev).forEach((entry) => {
        existingByName.set(entry.name.toLowerCase(), entry);
      });

      // Keep every already-selected exercise in its current plan position.
      // Rebuilding from the library here used to silently alphabetize/reorder
      // the whole plan whenever just one new exercise was added.
      const selected = [];
      const includedNames = new Set();
      prev.forEach((entry) => {
        const current = normalizePlanExercise(entry);
        const primaryKey = current.name.toLowerCase();
        if (!selectedSet.has(primaryKey)) return;

        const partnerKey = current.supersetExercise?.name?.toLowerCase();
        if (current.mode === 'SZ' && partnerKey && selectedSet.has(partnerKey)) {
          selected.push(current);
          includedNames.add(partnerKey);
        } else {
          const unpaired = { ...current, mode: current.mode === 'SZ' ? 'NR' : current.mode };
          delete unpaired.supersetExercise;
          selected.push(normalizePlanExercise(unpaired));
        }
        includedNames.add(primaryKey);
      });

      // Newly selected entries are appended in the order chosen by the user.
      exercisePickerSelection.forEach((name) => {
        const key = name.toLowerCase();
        if (includedNames.has(key)) return;
        const existing = normalizePlanExercise(existingByName.get(key) || { name, focus: exerciseFocus(name), weight: '' });
        selected.push(normalizePlanExercise({ ...existing, mode: 'NR' }));
        includedNames.add(key);
      });

      return normalizePlanExercises(selected);
    });
    setTrainingPlanSelected(exercisePickerSelection.length > 0);
    setSelectedDynamicSlots({});
    setExercisePickerSelection([]);
    setExerciseFilterInput('');
    setCustomExerciseInput('');
    setCustomExerciseError('');
    setCustomExerciseModalOpen(false);
    setExercisePickerOpen(false);
    setExercisePickerMode('plan');
  };

  const openCustomExerciseModal = () => {
    setCustomExerciseError('');
    setCustomExerciseInput(normalizeExerciseName(exerciseFilterInput));
    setCustomExerciseFocus(exerciseFocusFilters.length === 1 ? exerciseFocusFilters[0] : 'full');
    setCustomExerciseModalOpen(true);
  };

  const addCustomExercise = () => {
    const name = normalizeExerciseName(customExerciseInput);
    if (!name) return;
    const existsInBase = Object.values(EXERCISE_LIBRARY)
      .some((list) => list.some((entry) => entry.toLowerCase() === name.toLowerCase()));
    const existsInCustom = customExercises.some((entry) => entry.name.toLowerCase() === name.toLowerCase());
    if (existsInBase || existsInCustom) {
      setCustomExerciseError(t('exerciseCustomExists'));
      return;
    }
    setCustomExercises((prev) => [...prev, { name, focus: customExerciseFocus }].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    setExerciseFocusFilters([]);
    setExerciseFilterInput('');
    setExercisePickerSelection((prev) => {
      if (exercisePickerMode !== 'dynamic') return [...prev, name];
      if (prev.length >= 2) return [prev[0], name];
      return [...prev, name];
    });
    setCustomExerciseInput('');
    setCustomExerciseError('');
    setCustomExerciseModalOpen(false);
  };

  const savePlanTemplate = () => {
    const title = String(planTitleInput || '').trim();
    if (!title) {
      setPlanTitleError(t('planNameRequired'));
      return;
    }
    if (planExercises.length === 0) return;
    setPlanTitleError('');
    const nextTemplate = {
      title,
      focus: derivedFocus,
      exercises: planExercises.map((exercise) => {
        const base = {
          name: exercise.name,
          focus: getExerciseFocus(exercise, customExercises),
          equipment: exercise.equipment || getAllowedEquipmentIds(exercise.name)[0],
          weight: sanitizeWeightInput(exercise.weight || '', true),
          mode: normalizeExerciseMode(exercise.mode)
        };
        if (base.mode === 'SZ' && exercise.supersetExercise?.name) {
          base.supersetExercise = {
            name: exercise.supersetExercise.name,
            focus: getExerciseFocus(exercise.supersetExercise, customExercises),
            equipment: exercise.supersetExercise.equipment || getAllowedEquipmentIds(exercise.supersetExercise.name)[0],
            weight: sanitizeWeightInput(exercise.supersetExercise.weight || '', true)
          };
        }
        return base;
      })
    };
    if (editingPlanIndex !== null) {
      setPlanLibrary((prev) => prev.map((entry, i) => i === editingPlanIndex ? nextTemplate : entry));
    } else {
      setPlanLibrary((prev) => {
        const filtered = prev.filter((entry) => entry.title.toLowerCase() !== title.toLowerCase());
        return [nextTemplate, ...filtered].slice(0, 20);
      });
    }
    setSessionTitle(title);
    setPlanEditMode('list');
    setEditingPlanIndex(null);
  };

  const movePlanExercise = (index, direction) => {
    setPlanExercises((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      const temp = next[index];
      next[index] = next[targetIndex];
      next[targetIndex] = temp;
      return next;
    });
  };

  const shufflePlanExercises = () => {
    setPlanExercises((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
      }
      return next;
    });
  };

  const syncActiveLibraryPlanExercises = (nextExercises) => {
    if (activePlanLibraryIndex == null) return;
    setPlanLibrary((prev) => {
      if (activePlanLibraryIndex < 0 || activePlanLibraryIndex >= prev.length) return prev;
      const target = prev[activePlanLibraryIndex];
      if (!target || target.title !== planTitleInput) return prev;
      const updated = [...prev];
      updated[activePlanLibraryIndex] = { ...target, exercises: nextExercises };
      return updated;
    });
  };

  const updatePlanExerciseField = (index, field, value, finalizeWeight = false) => {
    let snapshotExercises = null;
    setPlanExercises((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      const current = normalizePlanExercise(next[index]);
      if (field === 'equipment') {
        const allowed = getAllowedEquipmentIds(current.name);
        next[index] = normalizePlanExercise({ ...current, equipment: allowed.includes(value) ? value : allowed[0] });
      } else if (field === 'weight') {
        next[index] = normalizePlanExercise({ ...current, weight: sanitizeWeightInput(value, finalizeWeight) });
      } else if (field === 'mode') {
        const newMode = normalizeExerciseMode(value);
        if (newMode === 'SZ') {
          if (current.supersetExercise?.name) {
            next[index] = normalizePlanExercise({ ...current, mode: 'SZ' });
          } else {
            const nextExercise = normalizePlanExercise(next[index + 1]);
            const supersetExercise = toSupersetExercise(nextExercise);
            if (!supersetExercise) return prev;
            const releasedNestedSuperset = nextExercise.mode === 'SZ' && nextExercise.supersetExercise?.name
              ? normalizePlanExercise({ ...nextExercise.supersetExercise, mode: 'NR' })
              : null;
            next[index] = normalizePlanExercise({ ...current, mode: 'SZ', supersetExercise });
            next.splice(index + 1, 1);
            if (releasedNestedSuperset) {
              next.splice(index + 1, 0, releasedNestedSuperset);
            }
          }
        } else {
          const releasedSuperset = current.supersetExercise?.name
            ? normalizePlanExercise({ ...current.supersetExercise, mode: 'NR' })
            : null;
          const updated = { ...current, mode: newMode };
          delete updated.supersetExercise;
          next[index] = normalizePlanExercise(updated);
          if (releasedSuperset) {
            next.splice(index + 1, 0, releasedSuperset);
          }
        }
      } else if (field === 'supersetName') {
        const szName = normalizeExerciseName(value);
        const szAllowed = getAllowedEquipmentIds(szName);
        next[index] = normalizePlanExercise({
          ...current,
          supersetExercise: { name: szName, equipment: szAllowed[0], weight: current.supersetExercise?.weight || '' }
        });
      } else if (field === 'supersetEquipment') {
        const sz = current.supersetExercise || {};
        const szAllowed = getAllowedEquipmentIds(sz.name || '');
        next[index] = normalizePlanExercise({
          ...current,
          supersetExercise: { ...sz, equipment: szAllowed.includes(value) ? value : szAllowed[0] }
        });
      } else if (field === 'supersetWeight') {
        const sz = current.supersetExercise || {};
        next[index] = normalizePlanExercise({
          ...current,
          supersetExercise: { ...sz, weight: sanitizeWeightInput(value, finalizeWeight) }
        });
      }
      snapshotExercises = next;
      return next;
    });

    if (snapshotExercises) {
      syncActiveLibraryPlanExercises(snapshotExercises);
    }

    if (sessionId && !offlineMode) {
      window.setTimeout(() => savePlan(sessionId), 0);
    }
  };

  const updateCurrentExerciseField = (field, value) => {
    if (currentPlanExerciseIndex < 0) return;
    const slotIndex = getCurrentPlanSlotIndex(config, state, planExercises.length);
    const baseExercise = planExercises[slotIndex];
    const isSupersetPause = state?.phase === 'supersetPause';
    const effectiveSegment = isSupersetPause ? 2 : (state?.segmentInSet || 1);
    const isSzSeg2 = normalizeExerciseMode(baseExercise?.mode) === 'SZ'
      && effectiveSegment === 2
      && baseExercise?.supersetExercise?.name;
    if (isSzSeg2 && field === 'equipment') {
      updatePlanExerciseField(slotIndex, 'supersetEquipment', value);
    } else if (isSzSeg2 && field === 'weight') {
      updatePlanExerciseField(slotIndex, 'supersetWeight', value);
    } else {
      updatePlanExerciseField(currentPlanExerciseIndex, field, value);
    }
  };

  const deletePlanTemplate = (index) => {
    setPlanLibrary((prev) => prev.filter((_, i) => i !== index));
    setActivePlanLibraryIndex((prev) => {
      if (prev == null) return prev;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  const openEditPlan = (index) => {
    const template = planLibrary[index];
    setPlanTitleInput(template.title || '');
    setPlanFocus(template.focus || 'full');
    setPlanExercises(normalizePlanExercises(template.exercises));
    setTrainingPlanSelected(Boolean(template.exercises?.length));
    setSelectedDynamicSlots({});
    setEditingPlanIndex(index);
    setActivePlanLibraryIndex(null);
    setPlanEditMode('form');
  };

  const openPlanOverlay = () => {
    setPlanEditMode('list');
    setEditingPlanIndex(null);
    setPlanOverlayOpen(true);
  };

  const closePlanOverlay = () => {
    setPlanOverlayOpen(false);
    setExercisePickerOpen(false);
    setExercisePickerMode('plan');
    setExercisePickerSelection([]);
    setExerciseFilterInput('');
    setCustomExerciseInput('');
    setCustomExerciseError('');
    setCustomExerciseModalOpen(false);
    setPlanEditMode('list');
    setEditingPlanIndex(null);
  };

  const loadPlanTemplate = (template, libraryIndex = null) => {
    setSessionTitle(template.title || getDefaultSessionTitle(language));
    setPlanTitleInput(template.title || '');
    setPlanFocus(template.focus || 'full');
    const nextExercises = normalizePlanExercises(template.exercises);
    setPlanExercises(nextExercises);
    setTrainingPlanSelected(Boolean(nextExercises.length));
    setSelectedDynamicSlots({});
    setActivePlanLibraryIndex(libraryIndex);
    const shouldOpenEditor = !configEditorOpen;
    setConfigEditorForm((prev) => {
      const base = shouldOpenEditor ? { ...lastIndividual, name: 'individual' } : prev;
      return nextExercises.length > 0 ? { ...base, rounds: nextExercises.length } : base;
    });
    if (shouldOpenEditor) {
      setIsController(true);
      setConfigEditorMode('individuell');
      setConfigEditorOpen(true);
    }
    closePlanOverlay();
  };

  const clearCurrentPlanSelection = async () => {
    setSessionTitle(getDefaultSessionTitle(language));
    setPlanTitleInput('');
    setPlanFocus('full');
    setPlanExercises([]);
    setTrainingPlanSelected(false);
    setSelectedDynamicSlots({});
    setActivePlanLibraryIndex(null);
    syncDirtyRef.current = true;
    if (!sessionId || !serverPersistenceEnabled) return;
    await syncSnapshotToServer(sessionId, {
      ...(snapshotRef.current || {}),
      sessionId,
      config,
      title: getDefaultSessionTitle(language),
      plan: null
    });
  };

  const onJoinDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...joinDigits];
    next[index] = digit;
    setJoinDigits(next);
    if (digit && index < 3) {
      joinInputRefs.current[index + 1]?.focus();
    }
  };

  const onJoinKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !joinDigits[index] && index > 0) {
      joinInputRefs.current[index - 1]?.focus();
    }
  };

  const attemptJoin = () => {
    joinSession(joinDigits.join(''), 'timer', true);
  };

  const timerClass = state?.phase === 'active'
    ? 'timerDisplay timerDisplay--active'
    : state?.phase === 'countdown'
      ? 'timerDisplay timerDisplay--countdown'
    : state?.phase === 'supersetPause'
      ? 'timerDisplay timerDisplay--supersetPause'
      : (state?.phase === 'rest' || state?.phase === 'roundRest')
      ? 'timerDisplay timerDisplay--rest'
      : 'timerDisplay';

  const timerControlsEl = isController && (
    <Stack direction="row" spacing={1} justifyContent="center" className="timerControls">
      <TimerButton enabled onClick={handlePrevControl} buttonType="prev" ariaLabel={t('controlPrev')} iconColor={isLightTheme ? '#1f2937' : '#f7f7f7'} />
      {!state?.running && <TimerButton enabled onClick={() => handleControl('play')} buttonType="play" ariaLabel={t('controlPlay')} iconColor={isLightTheme ? '#1f2937' : '#f7f7f7'} />}
      {state?.running && <TimerButton enabled onClick={() => handleControl('pause')} buttonType="pause" ariaLabel={t('controlPause')} iconColor={isLightTheme ? '#1f2937' : '#f7f7f7'} />}
      <TimerButton enabled onClick={() => handleControl('skip')} buttonType="skip" ariaLabel={t('controlSkip')} iconColor={isLightTheme ? '#1f2937' : '#f7f7f7'} />
    </Stack>
  );

  // eslint-disable-next-line no-unused-vars
  const openJoinFromSurface = () => {
    setIsController(true);
    setJoinError('');
    setJoinDigits(['', '', '', '']);
    setJoinOverlayOpen(true);
  };

  return (
    <div className="App" data-theme={resolvedTheme}>
      {shouldKeepScreenAwake && !supportsScreenWakeLock && !wakeFallbackActive && (
        <div className="miniOverlayWrap" style={{ zIndex: 1600 }}>
          <Paper className="miniOverlay">
            <div className="miniOverlayTitle hiitFont">{t('wakeLockUnlock')}</div>
            <Button className="hiitButton" onTouchStart={activateWakeFallback} onClick={activateWakeFallback}>{t('wakeLockUnlock')}</Button>
          </Paper>
        </div>
      )}
      <SharedPlanDialog apiBase={API_BASE} t={t} focusMeta={focusMeta} canImport={librariesReady && account.status !== 'loading'} onImport={(plan) => {
        setPlanLibrary((prev) => [plan, ...prev].slice(0, 200));
        setPlanOverlayOpen(true);
        setPlanEditMode('list');
      }} />
      {shareDialog && (
        <div className="miniOverlayWrap" onClick={() => setShareDialog(null)}>
          <Paper className="miniOverlay" onClick={(event) => event.stopPropagation()}>
            <div className="miniOverlayHead"><div className="miniOverlayTitle">{t('sharePlan')}</div><IconButton aria-label={t('ariaClose')} onClick={() => setShareDialog(null)}><CloseIcon /></IconButton></div>
            <p>{t('shareHint')}</p>
            <input className="configNumber shareUrl" aria-label={t('sharePlan')} readOnly value={shareDialog.url} onFocus={(event) => event.target.select()} />
            <Button className="hiitButton" onClick={async () => {
              try { await navigator.clipboard.writeText(shareDialog.url); setShareDialog((prev) => ({ ...prev, copied: true })); }
              catch (_error) { /* The selectable link remains available. */ }
            }}>{t(shareDialog.copied ? 'linkCopied' : 'copyLink')}</Button>
            <Button className="hiitButton" onClick={async () => {
              try { await account.request(`/api/shared-plans/${shareDialog.id}`, { method: 'DELETE' }); setShareDialog(null); }
              catch (_error) { setShareError(t('shareFailed')); }
            }}>{t('revokeLink')}</Button>
            {shareError && <p role="alert">{shareError}</p>}
          </Paper>
        </div>
      )}
      {settingsOpen && (
        <div className="miniOverlayWrap" onClick={() => setSettingsOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayHead">
              <div className="miniOverlayTitle hiitFont">{t('settingsLanguage')}</div>
              <IconButton className="miniOverlayClose" size="small" onClick={() => setSettingsOpen(false)} aria-label={t('ariaCloseSettings')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            <Stack spacing={1}>
              <Button className="hiitButton settingsLangButton" onClick={() => selectLanguage('de')}>{t('languageGerman')}</Button>
              <Button className="hiitButton settingsLangButton" onClick={() => selectLanguage('en')}>{t('languageEnglish')}</Button>
              <Button className="hiitButton settingsLangButton" onClick={() => selectLanguage('es')}>{t('languageSpanish')}</Button>
              <Button className="hiitButton settingsLangButton" onClick={() => selectLanguage('pt')}>{t('languagePortuguese')}</Button>
            </Stack>
            <AccountPanel account={account} t={t} />
            <div className="settingsSectionTitle hiitFont">{t('settingsDesign')}</div>
            <div className="themeSwatchRow">
              <button type="button" className={`themeSwatch themeSwatch--dark ${themeMode === 'dark' ? 'themeSwatch--active' : ''}`} aria-label={t('themeDark')} onClick={() => selectThemeMode('dark')} />
              <button type="button" className={`themeSwatch themeSwatch--light ${themeMode === 'light' ? 'themeSwatch--active' : ''}`} aria-label={t('themeLight')} onClick={() => selectThemeMode('light')} />
              <button type="button" className={`themeSwatch themeSwatch--auto ${themeMode === 'auto' ? 'themeSwatch--active' : ''}`} aria-label={t('themeAuto')} onClick={() => selectThemeMode('auto')} />
            </div>
          </Paper>
        </div>
      )}

      {trainingLogOpen && (
        <div className="miniOverlayWrap" onClick={() => setTrainingLogOpen(false)}>
          <Paper className="miniOverlay trainingLogOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayHead">
              <div className="miniOverlayTitle hiitFont">{t('trainingLog')}</div>
              <IconButton className="miniOverlayClose" size="small" onClick={() => setTrainingLogOpen(false)} aria-label={t('ariaClose')}><CloseIcon fontSize="small" /></IconButton>
            </div>
            {trainingLog.length === 0 ? (
              <div className="trainingLogEmpty hiitFont">{t('trainingLogEmpty')}</div>
            ) : (
              <>
                <div className="trainingLogList">
                  {trainingLog.map((entry) => {
                    const d = new Date(entry.completedAt);
                    const dateStr = `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
                    const timeStr = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                    return (
                      <div key={entry.id} className="trainingLogEntry">
                        <div className="trainingLogEntryMain">
                          <div className="trainingLogTitle hiitFont">
                            {entry.planTitle || '—'}
                            {(entry.repeatCount || 1) > 1 && (
                              <span className="trainingLogRepeat hiitFont"> ×{entry.repeatCount}</span>
                            )}
                          </div>
                          <div className="trainingLogDate hiitFont">{dateStr} {timeStr}</div>
                          <div className="trainingLogDetails hiitFont">
                            {t('trainingLogExercises', { n: (entry.exercises || []).length })}
                            {' · '}
                            {entry.config?.rounds || 1}R × {entry.config?.sets || 1}S
                          </div>
                        </div>
                        <IconButton size="small" className="trainingLogDeleteBtn" onClick={() => setTrainingLog((prev) => prev.filter((e) => e.id !== entry.id))} aria-label={t('trainingLogDelete')}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    );
                  })}
                </div>
                <Button className="hiitButton trainingLogClearBtn" onClick={() => setTrainingLog([])}>{t('trainingLogClearAll')}</Button>
              </>
            )}
          </Paper>
        </div>
      )}

      {joinOverlayOpen && (
        <div className="miniOverlayWrap" onClick={() => setJoinOverlayOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayHead">
              <div className="miniOverlayTitle hiitFont">{t('menuJoinSession')}</div>
              <IconButton className="miniOverlayClose" size="small" onClick={() => setJoinOverlayOpen(false)} aria-label={t('ariaClose')}><CloseIcon /></IconButton>
            </div>
            {!canJoinSession && <div className="joinError">{t('offlineStatus')}</div>}
            <div className="joinBoxes">
              {joinDigits.map((digit, index) => (
                <input
                  key={`join-${index}`}
                  ref={(el) => { joinInputRefs.current[index] = el; }}
                  className="joinDigit hiitFont"
                  value={digit}
                  inputMode="numeric"
                  maxLength={1}
                  disabled={!canJoinSession}
                  aria-label={t('ariaJoinDigit', { index: index + 1 })}
                  onChange={(e) => onJoinDigitChange(index, e.target.value)}
                  onKeyDown={(e) => onJoinKeyDown(index, e)}
                />
              ))}
            </div>
            <Button className="hiitButton" disabled={!canJoinSession} onClick={attemptJoin}>{t('join')}</Button>
            {joinError && <div className="joinError">{joinError}</div>}
          </Paper>
        </div>
      )}

      {deviceOverlayOpen && (
        <div className="miniOverlayWrap" onClick={() => setDeviceOverlayOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayHead">
              <div className="miniOverlayTitle hiitFont">{t('deviceCouple')}</div>
              <IconButton className="miniOverlayClose" size="small" onClick={() => setDeviceOverlayOpen(false)} aria-label={t('ariaClose')}><CloseIcon /></IconButton>
            </div>
            {!canUseOnlineFeatures && <div className="joinError">{t('offlineStatus')}</div>}
            <div className="deviceCodeRow">
              <input
                className="configNumber hiitFont"
                type="text"
                inputMode="numeric"
                maxLength={4}
                disabled={!canUseOnlineFeatures}
                value={deviceCodeInput}
                onChange={(e) => {
                  setLinkMessage('');
                  setDeviceCodeInput(e.target.value.replace(/\D/g, '').slice(0, 4));
                }}
                placeholder={t('deviceCodePlaceholder')}
              />
              <Button className="hiitButton" disabled={!canUseOnlineFeatures || parseSessionId(deviceCodeInput).length !== 4} onClick={() => linkDeviceToCurrentSession(deviceCodeInput)}>{t('deviceAdd')}</Button>
            </div>
            {linkMessage && <div className="joinSuccess">{linkMessage}</div>}
          </Paper>
        </div>
      )}

      {templateOverlayOpen && (
        <div className="miniOverlayWrap miniOverlayWrap--top" onClick={() => setTemplateOverlayOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayHead">
              <div className="miniOverlayTitle hiitFont">{t('templateOverlayTitle')}</div>
              <IconButton className="miniOverlayClose" size="small" onClick={() => setTemplateOverlayOpen(false)} aria-label={t('ariaClose')}><CloseIcon /></IconButton>
            </div>
            <div className="templateSectionLabel hiitFont">{t('templateBuiltIns')}</div>
            <div className="templateList">
              {builtInTemplates.map((tpl) => (
                <button key={tpl.id} type="button" className="templateRow" onClick={() => loadTemplate(tpl)}>
                  <span className="templateRowTitle hiitFont">{tpl.title}</span>
                </button>
              ))}
            </div>
            <div className="templateSectionLabel hiitFont">{t('templateUserSaved')}</div>
            {templateLibrary.length === 0 ? (
              <div className="templateEmptyMsg">{t('templateEmpty')}</div>
            ) : (
              <div className="templateList">
                {templateLibrary.map((tpl) => (
                  <div key={tpl.id} className="templateRow templateRow--user">
                    <button type="button" className="templateRowMain" onClick={() => loadTemplate(tpl)}>
                      <span className="templateRowTitle hiitFont">{tpl.title}</span>
                    </button>
                    <IconButton size="small" className="templateRowDelete" onClick={() => deleteTemplate(tpl.id)} aria-label={t('ariaDeleteTemplate')}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </Paper>
        </div>
      )}

      {templateSavePromptOpen && (
        <div className="miniOverlayWrap miniOverlayWrap--top" onClick={() => setTemplateSavePromptOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayHead">
              <div className="miniOverlayTitle hiitFont">{t('saveAsTemplate')}</div>
              <IconButton className="miniOverlayClose" size="small" onClick={() => setTemplateSavePromptOpen(false)} aria-label={t('ariaClose')}><CloseIcon /></IconButton>
            </div>
            <input
              className="configNumber hiitFont"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 8 }}
              type="text"
              placeholder={t('templateNamePlaceholder')}
              value={templateNameInput}
              onChange={(e) => { setTemplateNameInput(e.target.value.slice(0, 60)); setTemplateNameError(''); }}
              autoFocus
            />
            {templateNameError && <div className="joinError" style={{ marginTop: 8 }}>{templateNameError}</div>}
            <div className="editorActions3">
              <Button className="hiitButton" onClick={saveCurrentAsTemplate}>{t('save')}</Button>
            </div>
          </Paper>
        </div>
      )}

      {configEditorOpen && (
        <div className="miniOverlayWrap" onClick={() => setConfigEditorOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayHead">
              <div className="miniOverlayTitle hiitFont">{t('editorTrainingTitle')}</div>
              <IconButton className="miniOverlayClose" size="small" onClick={() => setConfigEditorOpen(false)} aria-label={t('ariaClose')}><CloseIcon /></IconButton>
            </div>
            <div className="configEditorPlanRow">
              <Button className={`hiitButton ${planExercises.length ? 'planSelectedGhost' : ''}`} onClick={openPlanOverlay}>{planButtonLabel}</Button>
              <Button className="hiitButton" onClick={() => setTemplateOverlayOpen(true)}>{t('templateButtonLabel')}</Button>
            </div>
            <Stack spacing={1} className="configRows">
              <label className="configRow"><span className="hiitFont">{t('configRounds')}</span><input className="configNumber hiitFont" type="number" value={configEditorForm.rounds} onChange={(e) => setConfigEditorForm({ ...configEditorForm, rounds: Number(e.target.value) })} /></label>
              <label className="configRow"><span className="hiitFont">{t('configSets')}</span><input className="configNumber hiitFont" type="number" value={configEditorForm.sets} onChange={(e) => setConfigEditorForm({ ...configEditorForm, sets: Number(e.target.value) })} /></label>
              <label className="configRow"><span className="hiitFont">{t('configActive')}</span><input className="configNumber hiitFont" type="number" value={Math.floor((configEditorForm.activeMs || 0) / 1000)} onChange={(e) => setConfigEditorForm({ ...configEditorForm, activeMs: Number(e.target.value) * 1000 })} /></label>
              <label className="configRow"><span className="hiitFont">{t('configRest')}</span><input className="configNumber hiitFont" type="number" value={Math.floor((configEditorForm.restMs || 0) / 1000)} onChange={(e) => setConfigEditorForm({ ...configEditorForm, restMs: Number(e.target.value) * 1000 })} /></label>
              <label className="configRow"><span className="hiitFont">{t('configRoundRest')}</span><input className="configNumber hiitFont" type="number" value={Math.floor((configEditorForm.roundRestMs || 0) / 1000)} onChange={(e) => setConfigEditorForm({ ...configEditorForm, roundRestMs: Number(e.target.value) * 1000 })} /></label>
              <label className="configRow configRow--switch"><span className="hiitFont">{t('configSuperset')}</span><Switch className="configToggle" checked={Boolean(configEditorForm.supersetEnabled)} onChange={(e) => setConfigEditorForm({ ...configEditorForm, supersetEnabled: e.target.checked })} /></label>
              <label className="configRow configRow--switch"><span className="hiitFont">{t('configDynamicTraining')}</span><Switch className="configToggle" checked={Boolean(configEditorForm.dynamicTraining)} onChange={(e) => setConfigEditorForm({ ...configEditorForm, dynamicTraining: e.target.checked })} /></label>
            </Stack>
            {snapshot?.scheduledStartAt && scheduleRemainingMs > 0 && (
              <div className="joinSuccess">{t('globalStartIn', { countdown: scheduleCountdown })}</div>
            )}
            <div className="editorActions3">
              <Button className="hiitButton" onClick={() => { setConfigEditorOpen(false); saveConfigAndShowTimer(); }}>{t('start')}</Button>
              <Button className="hiitButton planBackBtn" onClick={openSaveTemplatePrompt}>{t('saveAsTemplate')}</Button>
            </div>
          </Paper>
        </div>
      )}

      {planOverlayOpen && planEditMode === 'list' && (
        <div className="planFullScreen" onClick={closePlanOverlay}>
          <div className="planPanel" onClick={(e) => e.stopPropagation()}>
          <div className="screenTopBar">
            <span className="planFullScreenTitle hiitFont">{t('planOverlayTitle')}</span>
            <IconButton className="timerControlButton" onClick={closePlanOverlay} aria-label={t('ariaClose')}><CloseIcon /></IconButton>
          </div>
          {planLibrary.length === 0 && (
            <div className="planEmptyMsg">{t('planEmpty')}</div>
          )}
          {shareError && <p role="alert">{shareError}</p>}
          <div className="planTilesGrid">
            {planLibrary.map((template, index) => {
              const focusEmoji = (focusMeta[template.focus]?.emoji) || '🧍';
              const isSelected = selectedPlanIndex === index;
              return (
                <div key={index} className={`planTile ${isSelected ? 'planTile--selected' : ''}`}>
                  <button type="button" className="planTileMain" onClick={() => loadPlanTemplate(template, index)}>
                    <span className={`planFocusBadge planFocusBadge--${template.focus || 'full'}`}>{focusEmoji}</span>
                    <span className="planTileTitle hiitFont">{template.title}</span>
                  </button>
                  <div className="planTileActions">
                    <IconButton size="small" disabled={!account.user || sharingPlan || !isOnline} title={t(account.user ? 'sharePlan' : 'shareLoginRequired')} aria-label={t('sharePlan')} onClick={() => sharePlan(template)}><ShareIcon /></IconButton>
                    {isSelected && (
                      <IconButton size="small" onClick={clearCurrentPlanSelection} style={{ color: '#ef4444' }} aria-label={t('ariaClearPlan')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => openEditPlan(index)} style={{ color: '#a9b0c2' }} aria-label={t('ariaEditPlan')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => deletePlanTemplate(index)} style={{ color: '#ef4444' }} aria-label={t('ariaDeletePlan')}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="planListFooterActions">
            <IconButton className="planSmallAction" aria-label={t('ariaNewPlan')} onClick={() => { setPlanTitleInput(''); setPlanFocus('full'); setPlanExercises([]); setEditingPlanIndex(null); setActivePlanLibraryIndex(null); setPlanEditMode('form'); }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
          </div>
        </div>
      )}

      {planOverlayOpen && planEditMode === 'form' && (
        <div className="planFullScreen" onClick={closePlanOverlay}>
          <div className="planPanel" onClick={(e) => e.stopPropagation()}>
          <div className="screenTopBar">
            <IconButton className="timerControlButton" onClick={() => { setPlanEditMode('list'); setEditingPlanIndex(null); setExercisePickerOpen(false); }} aria-label={t('ariaClose')}>
              <ArrowBackIcon />
            </IconButton>
            <span className="planFullScreenTitle hiitFont">{editingPlanIndex !== null ? t('planEditTitle') : t('planNewTitle')}</span>
            <div style={{ width: 40 }} />
          </div>
          <input className="configNumber hiitFont" style={{ width: '100%', boxSizing: 'border-box', marginTop: 8 }} type="text" placeholder={t('planNamePlaceholder')} value={planTitleInput} aria-invalid={Boolean(planTitleError)} onChange={(e) => { setPlanTitleInput(e.target.value.slice(0, 80)); setPlanTitleError(''); }} />
          {planTitleError && <div className="joinError" style={{ marginTop: 8 }}>{planTitleError}</div>}
          <div className="planEditBody">
            {planExercises.length > 0 && (
              <div className="planExerciseListFull">
                {(() => {
                  let setNum = 0;
                  const nodes = [];
                  planExercises.forEach((ex, i) => {
                    const mode = normalizeExerciseMode(ex.mode);
                    const canPairAsSuperset = i < planExercises.length - 1 || Boolean(ex.supersetExercise?.name);
                    const isPaired = mode === 'SZ' && Boolean(ex.supersetExercise?.name);
                    setNum += 1;
                    const mainLabel = isPaired ? `${setNum}A` : `${setNum}`;
                    const partnerLabel = isPaired ? `${setNum}B` : null;
                    const overflow = i >= requiredExerciseCount;

                    nodes.push(
                      <div key={`main-${i}`} className={`planExerciseRow ${overflow ? 'planExerciseRow--overflow' : ''}`}>
                        <div className="planExerciseMain">
                          <a
                            className="exerciseLink hiitFont"
                            href={getExerciseReferenceUrl(ex.name)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {overflow
                              ? `✕ ${formatExerciseLabel(ex.name)}`
                              : `${mainLabel}. ${formatExerciseLabel(ex.name)}`}
                          </a>
                          <span className="exerciseFocusIcon" title={focusMeta[getExerciseFocus(ex, customExercises)].label} aria-label={focusMeta[getExerciseFocus(ex, customExercises)].label}>{focusMeta[getExerciseFocus(ex, customExercises)].emoji}</span>
                          <div className="planExerciseMetaInputs">
                            <select
                              className="planMetaSelect controlCompact hiitFont"
                              value={mode}
                              onChange={(e) => updatePlanExerciseField(i, 'mode', e.target.value)}
                            >
                              <option value="NR">NR</option>
                              <option value="SZ" disabled={!canPairAsSuperset}>SZ</option>
                              <option value="LR">LR</option>
                            </select>
                            <select
                              className="planMetaSelect controlCompact hiitFont"
                              value={ex.equipment || getAllowedEquipmentIds(ex.name)[0]}
                              onChange={(e) => updatePlanExerciseField(i, 'equipment', e.target.value)}
                            >
                              {getAllowedEquipmentIds(ex.name).map((id) => (
                                <option key={id} value={id}>{`${EQUIPMENT_OPTIONS[id].icon} ${EQUIPMENT_OPTIONS[id].short}`}</option>
                              ))}
                            </select>
                            <input
                              className="planMetaWeight controlCompact hiitFont"
                              type="text"
                              inputMode="decimal"
                              placeholder="kg"
                              value={ex.weight || ''}
                              onChange={(e) => updatePlanExerciseField(i, 'weight', e.target.value)}
                              onBlur={(e) => updatePlanExerciseField(i, 'weight', e.target.value, true)}
                            />
                          </div>
                        </div>
                        <div className="planExerciseActions">
                          <IconButton size="small" className="planExerciseMove" onClick={() => movePlanExercise(i, -1)} disabled={i === 0} aria-label={t('ariaMoveExerciseUp')}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" className="planExerciseMove" onClick={() => movePlanExercise(i, 1)} disabled={i === planExercises.length - 1} aria-label={t('ariaMoveExerciseDown')}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <button type="button" className="planExerciseRemove" onClick={() => setPlanExercises((prev) => prev.filter((_, j) => j !== i))}>✕</button>
                        </div>
                      </div>
                    );

                    if (isPaired) {
                      const partnerName = ex.supersetExercise.name;
                      const partnerAllowed = getAllowedEquipmentIds(partnerName);
                      nodes.push(
                        <div key={`partner-${i}`} className={`planExerciseRow planExerciseRow--partner ${overflow ? 'planExerciseRow--overflow' : ''}`}>
                          <div className="planExerciseMain">
                            <a
                              className="exerciseLink hiitFont"
                              href={getExerciseReferenceUrl(partnerName)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {overflow
                                ? `✕ ${formatExerciseLabel(partnerName)}`
                                : `${partnerLabel}. ${formatExerciseLabel(partnerName)}`}
                            </a>
                            <span className="exerciseFocusIcon" title={focusMeta[getExerciseFocus(ex.supersetExercise, customExercises)].label} aria-label={focusMeta[getExerciseFocus(ex.supersetExercise, customExercises)].label}>{focusMeta[getExerciseFocus(ex.supersetExercise, customExercises)].emoji}</span>
                            <div className="planExerciseMetaInputs planExerciseMetaInputs--partner">
                              <span aria-hidden="true" />
                              <select
                                className="planMetaSelect controlCompact hiitFont"
                                value={ex.supersetExercise?.equipment || partnerAllowed[0]}
                                onChange={(e) => updatePlanExerciseField(i, 'supersetEquipment', e.target.value)}
                              >
                                {partnerAllowed.map((id) => (
                                  <option key={id} value={id}>{`${EQUIPMENT_OPTIONS[id].icon} ${EQUIPMENT_OPTIONS[id].short}`}</option>
                                ))}
                              </select>
                              <input
                                className="planMetaWeight controlCompact hiitFont"
                                type="text"
                                inputMode="decimal"
                                placeholder="kg"
                                value={ex.supersetExercise?.weight || ''}
                                onChange={(e) => updatePlanExerciseField(i, 'supersetWeight', e.target.value)}
                                onBlur={(e) => updatePlanExerciseField(i, 'supersetWeight', e.target.value, true)}
                              />
                            </div>
                          </div>
                          <div className="planExerciseActions">
                            <button type="button" className="planExerciseRemove" onClick={() => updatePlanExerciseField(i, 'mode', 'NR')} aria-label={t('ariaCancel')}>✕</button>
                          </div>
                        </div>
                      );
                    }
                  });
                  return nodes;
                })()}
              </div>
            )}
            {planExercises.length > 0 && (
              <div className="planFocusDerived">
                <span className={`planFocusBadge planFocusBadge--${derivedFocus}`}>
                  {`${(focusMeta[derivedFocus]?.emoji) || '🧍'} ${(focusMeta[derivedFocus]?.label) || 'full'}`}
                </span>
              </div>
            )}
          </div>
          <div className="planIconActions">
            <IconButton className="planIconButton" aria-label={t('ariaAddExercise')} onClick={openExercisePicker}>
              <AddIcon />
            </IconButton>
            <IconButton className="planIconButton" aria-label={t('ariaSavePlan')} onClick={savePlanTemplate}>
              <SaveIcon />
            </IconButton>
            <IconButton className="planIconButton" aria-label={t('ariaShuffleExercises')} onClick={shufflePlanExercises}>
              <ShuffleIcon />
            </IconButton>
          </div>
          </div>
        </div>
      )}

      {exercisePickerOpen && (
        <div className="miniOverlayWrap" onClick={() => { setExercisePickerOpen(false); setExercisePickerSelection([]); setExercisePickerMode('plan'); }}>
          <Paper className="miniOverlay miniOverlay--wide" onClick={(e) => e.stopPropagation()}>
            <div className="planPickerHeader">
              <div className="miniOverlayTitle hiitFont">{exercisePickerMode === 'dynamic' ? t('dynamicNextExercise') : t('planAddExercise')}</div>
              <div className="planPickerActions">
                {exercisePickerSelection.length > 0 && (
                  <span className="planPickerCount hiitFont">{exercisePickerSelection.length}</span>
                )}
                <IconButton className="planIconButton" aria-label={t('ariaSaveExerciseSelection')} onClick={applyExerciseSelection}>
                  <SaveIcon />
                </IconButton>
                <IconButton className="planIconButton planIconButton--cancel" aria-label={t('ariaCancel')} onClick={() => { setExercisePickerOpen(false); setExercisePickerSelection([]); setExercisePickerMode('plan'); }}>
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
            <div className="focusFilters" role="group" aria-label={t('focusFilter')}>
              {FOCUS_IDS.map((focus) => (
                <label key={focus} className={`focusFilter ${exerciseFocusFilters.includes(focus) ? 'focusFilter--active' : ''}`} title={focusMeta[focus].label}>
                  <input type="checkbox" aria-label={focusMeta[focus].label} checked={exerciseFocusFilters.includes(focus)} onChange={() => setExerciseFocusFilters((prev) => prev.includes(focus) ? prev.filter((item) => item !== focus) : [...prev, focus])} />
                  <span aria-hidden="true">{focusMeta[focus].emoji}</span>
                </label>
              ))}
            </div>
            <div className="exercisePickerFilterRow">
              <input
                className="configNumber controlCompact hiitFont"
                type="text"
                value={exerciseFilterInput}
                placeholder={t('exerciseFilterPlaceholder')}
                onChange={(e) => setExerciseFilterInput(e.target.value)}
              />
              <IconButton className="planIconButton" aria-label={t('exerciseCustomAdd')} onClick={openCustomExerciseModal}>
                <AddIcon />
              </IconButton>
            </div>
            {filteredExerciseOptions.length === 0 && <p role="status">{t('noExercises')}</p>}
            <div className="exercisePickerList">
              {filteredExerciseOptions.map((exercise) => {
                const selected = exercisePickerSelection.some((item) => item.toLowerCase() === exercise.toLowerCase());
                return (
                <button
                  key={exercise}
                  type="button"
                  className={`joinSearchItem exercisePickerItem ${selected ? 'exercisePickerItem--selected' : ''}`}
                  onClick={() => toggleExerciseSelection(exercise)}
                >
                  <span className="exercisePickerName"><span className="exerciseFocusIcon" title={focusMeta[exerciseFocus(exercise)].label} aria-label={focusMeta[exerciseFocus(exercise)].label}>{focusMeta[exerciseFocus(exercise)].emoji}</span> {formatExerciseLabel(exercise)}</span>
                  <span className="exercisePickerCheck">{selected ? t('selected') : t('select')}</span>
                </button>
                );
              })}
            </div>
            {customExerciseModalOpen && (
              <div className="exerciseCustomModal">
                <div className="miniOverlayTitle hiitFont">{t('exerciseCustomTitle')}</div>
                <input
                  className="configNumber controlCompact hiitFont"
                  type="text"
                  value={customExerciseInput}
                  placeholder={t('exerciseCustomName')}
                  onChange={(e) => setCustomExerciseInput(e.target.value.slice(0, 120))}
                />
                <div className="focusFilters" role="radiogroup" aria-label={t('exerciseFocus')}>
                  {FOCUS_IDS.map((focus) => (
                    <label key={focus} className={`focusFilter ${customExerciseFocus === focus ? 'focusFilter--active' : ''}`} title={focusMeta[focus].label}>
                      <input type="radio" name="customExerciseFocus" aria-label={focusMeta[focus].label} checked={customExerciseFocus === focus} onChange={() => setCustomExerciseFocus(focus)} />
                      <span aria-hidden="true">{focusMeta[focus].emoji}</span>
                    </label>
                  ))}
                </div>
                {customExerciseError && <div className="joinError">{customExerciseError}</div>}
                <div className="planCountWarnActions">
                  <Button className="hiitButton" onClick={addCustomExercise}>{t('exerciseCustomAdd')}</Button>
                  <Button className="hiitButton planBackBtn" onClick={() => setCustomExerciseModalOpen(false)}>{t('ariaCancel')}</Button>
                </div>
              </div>
            )}
          </Paper>
        </div>
      )}

      {scheduleOverlayOpen && (
        <div className="miniOverlayWrap" onClick={() => setScheduleOverlayOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayTitle hiitFont">{t('globalStart')}</div>
            <input
              className="configNumber hiitFont"
              type="datetime-local"
              value={scheduledStartInput}
              onChange={(e) => setScheduledStartInput(e.target.value)}
            />
            <Button
              className="hiitButton"
              onClick={async () => {
                await saveScheduledStart();
                setScheduleOverlayOpen(false);
              }}
            >
              {t('save')}
            </Button>
          </Paper>
        </div>
      )}

      {planCountWarningOpen && (
        <div className="miniOverlayWrap" onClick={() => setPlanCountWarningOpen(false)}>
          <Paper className="miniOverlay" onClick={(e) => e.stopPropagation()}>
            <div className="miniOverlayTitle hiitFont">{t('warningTitle')}</div>
            <div className="planCountWarnText">
              {t('exercisesCount', { current: planExercises.length, required: requiredExerciseCount })}
            </div>
            <div className="planCountWarnActions">
              <Button className="hiitButton" onClick={proceedWithFewerExercises}>{t('start')}</Button>
              <Button className="hiitButton planBackBtn" onClick={() => setPlanCountWarningOpen(false)}>{t('planBack')}</Button>
            </div>
          </Paper>
        </div>
      )}

      <main className={`main ${view === 'session' ? 'main--session' : ''} ${view === 'timer' && !isController ? 'main--slaveTimer' : ''} ${view === 'timer' ? 'main--timer' : ''}`}>
        {view === 'session' && (
          <div className="homeScreen">
            <div className="screenTopBar">
              <IconButton className="timerControlButton" onClick={toggleSound} aria-label={t('ariaSoundToggle')}>
                {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
              </IconButton>
              <IconButton className="timerControlButton" onClick={() => setSettingsOpen(true)} aria-label={t('ariaSettings')}>
                <SettingsIcon />
              </IconButton>
            </div>
            <div className="homeHeader">
              {!canUseOnlineFeatures && (
                <div className="homeOfflineLabel hiitFont">Offline</div>
              )}
              <img
                className="homeLogo"
                src={isLightTheme ? time2hiitLogoLight : time2hiitLogoFinal}
                alt="Time2HIIT"
              />
            </div>
            {sessionExpiredVisible && (
              <div className="sessionExpiredBanner hiitFont" role="status">
                <span>{t('sessionExpired')}</span>
                <IconButton size="small" className="sessionExpiredDismiss" onClick={() => setSessionExpiredVisible(false)} aria-label={t('ariaDismiss')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}
            <div className="homeGrid">
            <button className="homeTile homeTile--primary" onClick={() => openConfigEditor('individuell')}>
              <TimerIcon className="homeTileIcon" />
              <span className="homeTileLabel hiitFont">{t('start')}</span>
            </button>
            <button className="homeTile" onClick={() => setTemplateOverlayOpen(true)}>
              <TuneIcon className="homeTileIcon" />
              <span className="homeTileLabel hiitFont">{t('menuTemplates')}</span>
            </button>
            <button className={`homeTile homeTile--plans ${planExercises.length ? 'homeTile--planSelected' : ''}`} onClick={openPlanOverlay}>
              <FitnessCenterIcon className="homeTileIcon" />
              <span className="homeTileLabel hiitFont">{planButtonLabel}</span>
            </button>
            <button className="homeTile homeTile--log" onClick={() => setTrainingLogOpen(true)}>
              <HistoryIcon className="homeTileIcon" />
              <span className="homeTileLabel hiitFont">{t('trainingLog')}</span>
            </button>
            <button className="homeTile homeTile--device" disabled={!canUseOnlineFeatures} onClick={() => setDeviceOverlayOpen(true)}>
              <DevicesIcon className="homeTileIcon" />
              <span className="homeTileCode hiitFont">{sessionId || '----'}</span>
              <span className="homeTileHint hiitFont">{t('deviceAddHint')}</span>
            </button>
            </div>
          </div>
        )}

        {view === 'timer' && (
          <Paper className="card timerCard centeredCard">
            <div className="screenTopBar timerTopBar">
              <IconButton className="timerControlButton" onClick={toggleSound} aria-label={t('ariaSoundToggle')}>
                {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
              </IconButton>
              <IconButton className="timerControlButton" onClick={leaveSessionToHome} aria-label={t('ariaHome')}>
                <HomeIcon />
              </IconButton>
              <IconButton className="timerControlButton" onClick={() => setSettingsOpen(true)} aria-label={t('ariaSettings')}>
                <SettingsIcon />
              </IconButton>
            </div>
            <div className="timerTopStats hiitFont">
              <div>{t('roundLabel')} {state?.currentRound || 0}/{config.rounds}</div>
              <div>{t('setLabel')} {state?.currentSet || 0}/{config.sets}</div>
            </div>
            {state?.phase !== 'done' && (
              <div
                className="timerDial"
                style={{
                  '--progress-deg': phaseProgressDeg,
                  '--progress-color': timerProgressColor
                }}
              >
                {showSupersetBadge && (
                  <div className="timerSupersetBadge hiitFont">
                    {(currentExerciseMode === 'SZ' || currentExerciseMode === 'LR')
                      ? `${t('presetSuperset')} ${state?.segmentInSet || 1}/2`
                      : t('presetSuperset')}
                  </div>
                )}
                <div className={timerClass}>{formatMs(remainingMs)}</div>
                {timerControlsEl}
              </div>
            )}
            {currentPlanExercise && (
              <div className="currentExercisePanel">
                {currentPlanExercise && (
                  <>
                    <div className="currentExerciseDisplay" key={currentPlanExercise.name}>
                      <a
                        className="exerciseLink exerciseLink--current hiitFont"
                        href={getExerciseReferenceUrl(currentPlanExercise.name)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {focusMeta[getExerciseFocus(currentPlanExercise, customExercises)].emoji} {formatExerciseLabel(currentPlanExercise.name)}
                      </a>
                    </div>
                    <div className="currentExerciseMetaEditor">
                      <select
                        className="planMetaSelect controlCompact hiitFont"
                        value={currentPlanExercise.equipment || getAllowedEquipmentIds(currentPlanExercise.name)[0]}
                        onChange={(e) => updateCurrentExerciseField('equipment', e.target.value)}
                        disabled={!isController}
                      >
                        {getAllowedEquipmentIds(currentPlanExercise.name).map((id) => (
                          <option key={id} value={id}>{`${EQUIPMENT_OPTIONS[id].icon} ${EQUIPMENT_OPTIONS[id].short}`}</option>
                        ))}
                      </select>
                      <input
                        className="planMetaWeight controlCompact hiitFont"
                        type="text"
                        inputMode="decimal"
                        placeholder="kg"
                        value={currentPlanExercise.weight || ''}
                        onChange={(e) => updateCurrentExerciseField('weight', e.target.value)}
                        onBlur={(e) => updateCurrentExerciseField('weight', sanitizeWeightInput(e.target.value, true))}
                        disabled={!isController}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            {state?.phase === 'done' && (
              <>
                <div className="timerDisplay timerDisplay--done">
                  {completionMessageIndex === 0 ? t('trainingCompleteA') : t('trainingCompleteB')}
                </div>
                <div className="timerDoneActions">
                  <Button className="hiitButton" onClick={saveConfigAndShowTimer}>{t('restart')}</Button>
                  <Button className="hiitButton planBackBtn" onClick={leaveSessionToHome}>{t('menuHome')}</Button>
                </div>
              </>
            )}
            {isController && (
              <>
                {state?.phase === 'done' && timerControlsEl}
                <div className="timerRightPane">
                  <div className="timerDeviceAddRow">
                    {linkMessage && <div className="joinSuccess">{linkMessage}</div>}
                  </div>
                </div>
              </>
            )}
          </Paper>
        )}
      </main>

    </div>
  );
}

export default App;
